import { Component, HostListener, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { forkJoin, lastValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";
import {
  FeesMasterDetailsDTO,
  FeeStructureMasterDTO,
} from "../../BackOffice/fee-structure-master/fee-structure-master.interface";
import {
  StudentClassMappingDTO,
  StudentClassMappingReadByClassIdRequestDTO,
} from "../../BackOffice/student-master/student-class-mapping/student-class-mapping.interface";
import { ValueListItemReadByVlCodeRequestDTO } from "src/app/Common/ValueListService/value-list-item.interface";
import { FeeInstallmentMasterDTO } from "../../BackOffice/fee-installment-master/fee-installment-master.interface";
import { ToastrService } from "ngx-toastr";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { UserLoginService } from "src/app/login/userlogin.service";
import { UserAffiliationMappingService } from "../../SchoolUserMapping/user-affiliation-mapping.service";
import { ClassMasterService } from "../../BackOffice/class-master/class-master.service";
import { SectionMasterService } from "../../BackOffice/section-master/section-master.service";
import { StudentClassMappingService } from "../../BackOffice/student-master/student-class-mapping/student-class-mapping.service";
import { StudentFeeStructureMappingService } from "../../BackOffice/student-master/student-fee-structure-mapping/student-fee-structure-mapping.service";
import { FeeStructureMasterService } from "../../BackOffice/fee-structure-master/fee-structure-master.service";
import { SchoolInvoiceDetailService } from "../../EndUser/receipt-detail/receipt-detail.service";
import { SchoolInvoiceMasterService } from "../../EndUser/receipt-master/receipt-master.service";
import { ValueListService } from "src/app/Common/ValueListService/value-list.service";
import {
  ClassMasterDTO,
  ClassMasterGetPendingFeeBySchoolIdDTO,
  ClassMasterGetPendingFeeBySchoolIdRequestDTO,
  ClassMasterReadBySchoolIdRequestDTO,
} from "../../BackOffice/class-master/class-master.interface";
import { SectionMasterDTO } from "../../BackOffice/section-master/section-master.interface";
import { UserAffiliationMappingReadByUserIdRequestDTO } from "../../SchoolUserMapping/user-affiliation-mapping.interface";
import { FeeComponentMasterDTO } from "../../BackOffice/fee-component-master/fee-component-master.interface";
import { NotificationMasterService } from "src/app/Common/NotificationMaster/notificationMaster.service";
import {
  feeReminderEmail,
  FeeReminderEmailData,
  FeeReminderItem,
} from "../../SchoolEmailTemplates/parentFeeReminderTemplate";
import { environment } from "src/environment/environment";
import {
  NotificationMasterCreateRequestDTO,
  PushNotificationSendMessageRequestDTO,
} from "src/app/Common/NotificationMaster/notificationMaster.interface";
import { ActivatedRoute, Router } from "@angular/router";
import { ParentStudentMappingService } from "../../BackOffice/parent-master/children/parent-student-mapping.service";
import {
  ParentStudentMappingDTO,
} from "../../BackOffice/parent-master/children/parent-student-mapping.interface";

import {
  SchoolInvoiceMasterCreateRequestDTO,
  SchoolInvoiceMasterDTO,
} from "../../EndUser/receipt-master/receipt-master.interface";
import {
  SchoolInvoiceDetailCreateRequestDTO,
  SchoolInvoiceDetailUpdateRequestDTO,
} from "../../EndUser/receipt-detail/receipt-detail.interface";
import { PaymentGateway_InitializeCashPayment } from "src/app/GlobalVariable";

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface FeeGroup {
  feeType: string;
  fees: FeeStructureMasterDTO[];
}

// Combined mandatory installment is a display/payment allocation over existing real fee IDs.
export interface MandatoryFeeCharge {
  fee: FeeStructureMasterDTO;
  installment: FeeInstallmentMasterDTO | null;
  feeName: string;
  amount: number;
  paid: number;
  balance: number;
  lateFee: number;
  dueDate: string;
  components: FeeComponentMasterDTO[];
}

export interface MandatoryCombinedInstallment {
  installmentNumber: number;
  installmentName: string;
  charges: MandatoryFeeCharge[];
  amount: number;
  paid: number;
  balance: number;
  lateFee: number;
  dueDate: string;
}

export interface InstallmentPaymentStatus {
  paidAmount: number;
  isPaid: boolean;
  remainingAmount: number;
  lateFeeAmount: number;
  totalAmount: number;
  paymentMethod?: string;
  latestPaymentDate?: any;
  latestPaidItem?: any;
}

export interface FeePaymentHistoryRow {
  invoiceId: number;
  receiptNo: string;
  amount: number;
  paymentMethod: string;
  paymentDate: Date;
  remarks: string;
}

export interface FeeCollectionBill {
  invoiceId: number;
  invoiceNo: string;
  receiptNo: string;
  prrn: string;
  studentId: number;
  studentName: string;
  className: string;
  sectionName: string;
  schoolName: string;
  feeName: string;
  installmentName: string;
  installmentId: number;
  paymentMethod: string;
  amount: number;
  installmentAmount: number;
  paidAmount: number;
  remainingAmount: number;
  paidDate: Date;
  status: string;
  collectedBy: string;
  remarks: string;
  paymentHistory: FeePaymentHistoryRow[];
  feeBreakdown?: { feeName: string; installmentName: string; amount: number }[];
}

export interface FeePaymentAllocationRow {
  fee: FeeStructureMasterDTO;
  inst: FeeInstallmentMasterDTO;
  installmentAmount: number;
  lateFeeAmount: number;
  totalAmount: number;
  isOneTime?: boolean;
}

export interface StudentFeeView {
  student: StudentClassMappingDTO;
  feeGroups: FeeGroup[];
  mandatoryInstallmentRows: MandatoryCombinedInstallment[];
  feeStructureList: FeeStructureMasterDTO[];
  installmentPaymentStatus: Map<number, InstallmentPaymentStatus>;
  oneTimeFeePaymentStatus: Map<number, InstallmentPaymentStatus>;
  isLoading: boolean;
  noFeeAssigned: boolean;
  expandedFeeIds: Set<number>;
  grandTotal: number;
  grandPaid: number;
  grandDue: number;
  grandLateFees: number;
  progressPercent: number;
}
export interface SchoolEditablePaidRow {
  detailId: number;
  invoiceId: number;
  masterId: number;
  masterType: string;
  feeName: string;
  installmentName: string;
  paymentDate: Date;
  originalAmount: number;
  newAmount: number;
  source: any;
}

export type NotificationChannel =
  | "email"
  | "pushNotification"
  | "sms"
  | "whatsapp";

export type AdminCollectionMethod = "cash" | "qr" | "link";

// One locally tracked cash operation per school/student until payment is verified.
type PendingCashCollectionRecord = {
  schoolId: number;
  studentId: number;
  feeStructureId: number;
  amount: number;
  allocationSignature: string;
  invoiceId: number;
  idempotencyKey: string;
  stage: "creatingInvoice" | "creatingDetails" | "readyForCash";
};

export type BreadcrumbLevel = "classes" | "sections" | "students" | "feeDetail";

export type FeeManagementTab = "overview" | "students" | "receipts";

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: "app-student-fees",
  templateUrl: "./student-fees.component.html",
  styleUrls: ["./student-fees.component.css"],
})
export class StudentFeesComponent implements OnInit, OnDestroy {
  @ViewChild("feeBillModal") feeBillModalTemplate?: TemplateRef<any>;

  // ── Service Injections ────────────────────────────────────────────────────
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private userLoginService = inject(UserLoginService);
  private affiliationService = inject(UserAffiliationMappingService);
  private classMasterService = inject(ClassMasterService);
  private sectionMasterService = inject(SectionMasterService);
  private studentClassMappingService = inject(StudentClassMappingService);
  private studentFeeStructureMappingService = inject(
    StudentFeeStructureMappingService,
  );
  private feeStructureService = inject(FeeStructureMasterService);
  private schoolInvoiceDetailService = inject(SchoolInvoiceDetailService);
  private schoolInvoiceMasterService = inject(SchoolInvoiceMasterService);
  private http = inject(HttpClient);
  private valueListService = inject(ValueListService);
  private notificationMasterService = inject(NotificationMasterService);
  private parentStudentMappingService = inject(ParentStudentMappingService);

  User = inject(UserLoginService).User();

  // ── School Context ────────────────────────────────────────────────────────
  schoolId = 0;
  schoolName = "";
  isSchoolLoading = true;

  // ── Top-level Tab ─────────────────────────────────────────────────────────
  activeTab: FeeManagementTab = "overview";
  private enteredClassFromOverview = false;

  // ── Overview Tab Data ─────────────────────────────────────────────────────
  overviewFeeList: ClassMasterGetPendingFeeBySchoolIdDTO[] = [];
  isOverviewLoading = false;

  // ── Breadcrumb Navigation ─────────────────────────────────────────────────
  currentLevel: BreadcrumbLevel = "classes";

  // ── Class Level ───────────────────────────────────────────────────────────
  classList: ClassMasterDTO[] = [];
  isClassLoading = false;
  classSearchTerm = "";

  // ── Section Level ─────────────────────────────────────────────────────────
  selectedClass: ClassMasterDTO | null = null;
  sectionList: SectionMasterDTO[] = [];
  isSectionLoading = false;
  noSectionsFallback = false;

  // ── Student Level ─────────────────────────────────────────────────────────
  selectedSection: SectionMasterDTO | null = null;
  studentList: StudentClassMappingDTO[] = [];
  isStudentLoading = false;
  studentSearchTerm = "";
  feeTypeLabels: { [code: string]: string } = {};

  /**
   * Tracks which student IDs have pending fees.
   * On initial load all valid students are marked pending.
   * Once a student's fee detail loads and grand due = 0, their ID is removed.
   */
  pendingFeeStudentIds: Set<number> = new Set();

  // ── Fee Detail Level ──────────────────────────────────────────────────────
  selectedStudentFeeView: StudentFeeView | null = null;
  selectedFeeDisplayTab: "mandatory" | "other" = "mandatory";
  expandedMandatoryInstallmentNumbers: Set<number> = new Set<number>();
  expandedInstallmentDetailIds: Set<number> = new Set<number>();
  isFeeDetailLoading = false;

  // ── Fee Collecting Mode ───────────────────────────────────────────────────
  isCollectingMode = false;
  selectedCollectionInstallmentIds: Set<number> = new Set<number>();
  customCollectionAmount: number | null = null;
  customCollectionAmountText = "";
  isCustomCollectionAmountManual = false;
  customAmountExceedsMax = false;
  isNavigating = false;
  showPageLoader = false;
  receiptId = 0;
  selectedProductCode = "";

  // ── Admin / Owner Collection Modal ───────────────────────────────────────
  collectionModalRef!: NgbModalRef;
  selectedCollectionMethod: AdminCollectionMethod = "cash";
  pendingCollectionView: StudentFeeView | null = null;
  pendingCollectionFee: FeeStructureMasterDTO | null = null;
  generatedCollectionLink = "";
  generatedCollectionQrCodeUrl = "";
  isQrCodeReady = false;
  createdCollectionInvoice: SchoolInvoiceMasterDTO | null = null;
  feeCollectionBillByInstallmentId: Map<number, FeeCollectionBill> = new Map<number, FeeCollectionBill>();
  feeCollectionBillByFeeStructureId: Map<number, FeeCollectionBill> = new Map<number, FeeCollectionBill>();
  lastGeneratedBill: FeeCollectionBill | null = null;
  sessionReceiptsRegistry: Map<string, FeeCollectionBill> = new Map<string, FeeCollectionBill>();
  billModalRef!: NgbModalRef;
  isSendingFeeBill = false;
  isSendingPaymentLink = false;
paidEditStudentView: StudentFeeView | null = null;

paidEditRows: SchoolEditablePaidRow[] = [];

isPaidEditLoading = false;

isPaidEditSaving = false;

paidEditError = "";


  private paymentWindowRef: Window | null = null;
  private paymentRefreshPending = false;
  private paymentRefreshStartedAt = 0;
  private lastPaymentRefreshAt = 0;
  private paymentWindowWatcher: any = null;

  /**
   * Invoice detail create is intentionally stored as Pending by the backend.
   * Payment success is persisted on InvoiceMaster, so fee status resolution
   * must verify the parent invoice status before showing Paid/Bill.
   */
  private invoiceMasterLookupCache: Map<
    number,
    Promise<SchoolInvoiceMasterDTO | null>
  > = new Map();

  private paymentAllocationPlanCache: WeakMap<
    FeeStructureMasterDTO,
    { signature: string; plan: FeePaymentAllocationRow[] }
  > = new WeakMap();

  // ── Notification State ────────────────────────────────────────────────────
  notifyModalRef!: NgbModalRef;
  notifyTarget: "single" | "class" = "single";
  notifySingleStudent: StudentClassMappingDTO | null = null;
  studentParents: ParentStudentMappingDTO[] = [];
  notifyChannels: Set<NotificationChannel> = new Set(["email"]);
  isSendingNotification = false;
  bulkSendProgress = 0;
  bulkSendSentCount = 0;
  notifySubject = "Reminder: Pending Fee Payment";

  // ── Bulk Notify ───────────────────────────────────────────────────────────
  bulkNotifyStudents: StudentClassMappingDTO[] = [];

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────

  private route = inject(ActivatedRoute);
  private router = inject(Router);

  preselectedClassId = 0;
  preselectedSectionId = 0;
  preselectedStudentId = 0;
  isRestoringState = false;
ngOnInit(): void {
  this.route.queryParams.subscribe((params) => {

    if (params["reset"] === "overview") {
      this.activeTab = "overview";
      this.currentLevel = "classes";
      this.enteredClassFromOverview = false;

      this.selectedClass = null;
      this.selectedSection = null;
      this.selectedStudentFeeView = null;

      this.isCollectingMode = false;
      this.selectedCollectionInstallmentIds.clear();
      this.resetCustomCollectionAmountState();

      this.preselectedClassId = 0;
      this.preselectedSectionId = 0;
      this.preselectedStudentId = 0;
      this.isRestoringState = false;

      if (this.schoolId > 0) {
        this.loadOverviewFeeData();
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          reset: null,
          classId: null,
          sectionId: null,
          studentId: null
        },
        queryParamsHandling: "merge",
        replaceUrl: true
      });

      return;
    }

    this.preselectedClassId = Number(params["classId"] || 0);
    this.preselectedSectionId = Number(params["sectionId"] || 0);
    this.preselectedStudentId = Number(params["studentId"] || 0);

    this.isRestoringState =
      this.preselectedClassId > 0 ||
      this.preselectedSectionId > 0 ||
      this.preselectedStudentId > 0;
  });

  this.resolveSchoolId();
  this.getValueListItem("SFeeType");
  this.getProductCode();
  this.restorePaymentRefreshWatcherFromStorage();
}
  // ─────────────────────────────────────────────────────────────────────────
  // Tab Navigation
  // ─────────────────────────────────────────────────────────────────────────

  switchTab(tab: FeeManagementTab): void {
    this.enteredClassFromOverview = false;
    this.activeTab = tab;
    if (tab === 'overview' && this.schoolId > 0 && !this.isOverviewLoading) {
      this.loadOverviewFeeData();
    }
  }

  private loadOverviewFeeData(): void {
    if (!this.schoolId || this.schoolId <= 0) return;
    this.isOverviewLoading = true;
    const payload: ClassMasterGetPendingFeeBySchoolIdRequestDTO = { schoolId: this.schoolId };
    this.classMasterService.ClassMaster_GetPendingFeeBySchoolId(payload).subscribe({
      next: (res) => {
        // Normalize: backend sends classPaidFeeAmount / classPendingFeeAmount
        // Alias them so both field names work in templates
        this.overviewFeeList = (res || []).map((item: any) => ({
          ...item,
          paidFeeAmount: item.classPaidFeeAmount ?? item.paidFeeAmount ?? 0,
          pendingFeeAmount: item.classPendingFeeAmount ?? item.pendingFeeAmount ?? 0,
        }));
        this.isOverviewLoading = false;
      },
      error: () => {
        this.isOverviewLoading = false;
      }
    });
  }

  // ── Overview Helpers ──────────────────────────────────────────────────────

  get overviewTotalCollected(): number {
    return (this.overviewFeeList || []).reduce((s, item) => s + Number(item.paidFeeAmount || 0), 0);
  }

  get overviewTotalPending(): number {
    return (this.overviewFeeList || []).reduce((s, item) => s + Number(item.pendingFeeAmount || 0), 0);
  }

  get overviewTotalExpected(): number {
    return this.overviewTotalCollected + this.overviewTotalPending;
  }

  get overviewCollectionPercent(): number {
    return this.overviewTotalExpected
      ? Math.round((this.overviewTotalCollected / this.overviewTotalExpected) * 100)
      : 0;
  }

  getOverviewClassTotal(item: ClassMasterGetPendingFeeBySchoolIdDTO): number {
    return Number(item.paidFeeAmount || 0) + Number(item.pendingFeeAmount || 0);
  }

  getOverviewClassPercent(item: ClassMasterGetPendingFeeBySchoolIdDTO): number {
    const total = this.getOverviewClassTotal(item);
    return total ? Math.round((Number(item.paidFeeAmount || 0) / total) * 100) : 0;
  }

  selectClassFromOverview(item: ClassMasterGetPendingFeeBySchoolIdDTO): void {
    const matched = this.classList.find((c) => c.classId === item.classId);
    this.enteredClassFromOverview = true;
    this.activeTab = "students";
    this.currentLevel = "sections";
    if (matched) {
      this.selectClass(matched);
    } else {
      this.selectClass({
        classId: item.classId,
        className: item.className || "Class",
        schoolId: this.schoolId,
      } as ClassMasterDTO);
    }
  }

  refreshOverviewData(): void {
    this.overviewFeeList = [];
    this.loadOverviewFeeData();
  }

  getClassFeeOverview(classId: number): ClassMasterGetPendingFeeBySchoolIdDTO | undefined {
    return (this.overviewFeeList || []).find((item) => Number(item.classId) === Number(classId));
  }

  // ── Receipts Tab Helpers ──────────────────────────────────────────────────

  receiptSearchTerm = "";

  get sessionReceiptsList(): FeeCollectionBill[] {
    const map = new Map<string, FeeCollectionBill>(this.sessionReceiptsRegistry);
    this.feeCollectionBillByInstallmentId.forEach((b) => {
      const key = b?.receiptNo || String(b?.invoiceId || "");
      if (key) map.set(key, b);
    });
    this.feeCollectionBillByFeeStructureId.forEach((b) => {
      const key = b?.receiptNo || String(b?.invoiceId || "");
      if (key) map.set(key, b);
    });
    if (this.lastGeneratedBill) {
      const key = this.lastGeneratedBill.receiptNo || String(this.lastGeneratedBill.invoiceId || "");
      if (key) map.set(key, this.lastGeneratedBill);
    }
    return Array.from(map.values());
  }

  get filteredReceiptsList(): FeeCollectionBill[] {
    const term = (this.receiptSearchTerm || "").trim().toLowerCase();
    const list = this.sessionReceiptsList;
    if (!term) return list;
    return list.filter(
      (b) =>
        (b.receiptNo || "").toLowerCase().includes(term) ||
        (b.studentName || "").toLowerCase().includes(term) ||
        (b.className || "").toLowerCase().includes(term) ||
        (b.feeName || "").toLowerCase().includes(term) ||
        (b.paymentMethod || "").toLowerCase().includes(term)
    );
  }

  openReceiptBill(bill: FeeCollectionBill, modalTemplate?: any): void {
    this.openFeeBillModal(bill, modalTemplate);
  }

  ngOnDestroy(): void {
    this.stopPaymentWindowCloseWatcher();
  }

  @HostListener("window:focus")
  onWindowFocusAfterPayment(): void {
    this.tryAutoRefreshAfterPaymentReturn("window_focus");
  }

  @HostListener("document:visibilitychange")
  onDocumentVisibilityChangeAfterPayment(): void {
    if (document.visibilityState === "visible") {
      this.tryAutoRefreshAfterPaymentReturn("tab_visible");
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Payment Auto Refresh Helpers
  // ─────────────────────────────────────────────────────────────────────────

  private getActivePaymentStudentId(): number {
    return this.selectedStudentFeeView?.student?.userId || this.preselectedStudentId || 0;
  }

  private getActivePaymentClassId(): number {
    return this.selectedClass?.classId || this.preselectedClassId || 0;
  }

  private getPaymentRefreshStorageKey(): string {
    return `student-fees-payment-refresh-${this.schoolId || 0}-${this.getActivePaymentStudentId()}-${this.getActivePaymentClassId()}`;
  }

  private markPaymentRefreshPending(invoiceId?: number): void {
    this.paymentRefreshPending = true;
    this.paymentRefreshStartedAt = Date.now();

    localStorage.setItem(
      this.getPaymentRefreshStorageKey(),
      JSON.stringify({
        invoiceId: invoiceId || this.receiptId || 0,
        classId: this.getActivePaymentClassId(),
        studentId: this.getActivePaymentStudentId(),
        schoolId: this.schoolId,
        createdAt: this.paymentRefreshStartedAt,
      }),
    );

    this.startPaymentWindowCloseWatcher();
  }

  private readPaymentRefreshPending(): boolean {
    if (this.paymentRefreshPending) {
      return true;
    }

    const rawData = localStorage.getItem(this.getPaymentRefreshStorageKey());

    if (!rawData) {
      return false;
    }

    try {
      const data = JSON.parse(rawData);
      const createdAt = Number(data?.createdAt || 0);

      if (!createdAt || Date.now() - createdAt > 30 * 60 * 1000) {
        localStorage.removeItem(this.getPaymentRefreshStorageKey());
        return false;
      }

      this.paymentRefreshPending = true;
      this.paymentRefreshStartedAt = createdAt;
      return true;
    } catch {
      localStorage.removeItem(this.getPaymentRefreshStorageKey());
      return false;
    }
  }

  private clearPaymentRefreshPending(): void {
    this.paymentRefreshPending = false;
    this.paymentRefreshStartedAt = 0;
    localStorage.removeItem(this.getPaymentRefreshStorageKey());
    this.stopPaymentWindowCloseWatcher();
  }

  private restorePaymentRefreshWatcherFromStorage(): void {
    if (this.readPaymentRefreshPending()) {
      this.startPaymentWindowCloseWatcher();
    }
  }

  private startPaymentWindowCloseWatcher(): void {
    this.stopPaymentWindowCloseWatcher();

    this.paymentWindowWatcher = setInterval(() => {
      const hasPendingPaymentRefresh = this.readPaymentRefreshPending();

      if (!hasPendingPaymentRefresh) {
        this.stopPaymentWindowCloseWatcher();
        return;
      }

      if (this.paymentWindowRef && this.paymentWindowRef.closed) {
        this.tryAutoRefreshAfterPaymentReturn("payment_tab_closed");
      }
    }, 1500);
  }

  private stopPaymentWindowCloseWatcher(): void {
    if (this.paymentWindowWatcher) {
      clearInterval(this.paymentWindowWatcher);
      this.paymentWindowWatcher = null;
    }
  }

  private tryAutoRefreshAfterPaymentReturn(_source: string): void {
    if (!this.readPaymentRefreshPending()) {
      return;
    }

    const now = Date.now();

    if (now - this.paymentRefreshStartedAt < 2500) {
      return;
    }

    if (now - this.lastPaymentRefreshAt < 3000) {
      return;
    }

    this.lastPaymentRefreshAt = now;
    this.clearPaymentRefreshPending();
    this.refreshFeeDetailsAfterPayment();
  }

  private refreshFeeDetailsAfterPayment(): void {
    const currentStudent = this.selectedStudentFeeView?.student;

    if (!currentStudent?.userId || !this.selectedClass?.classId) {
      return;
    }

    const keepCollectingMode = this.isCollectingMode;

    this.showPageLoader = true;
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();

    this.openStudentFeeDetail(currentStudent, keepCollectingMode);

    setTimeout(() => {
      this.openStudentFeeDetail(currentStudent, keepCollectingMode);
      this.showPageLoader = false;
    }, 3500);
  }

  private isPaidStatus(value: any): boolean {
    const normalizedStatus = String(value || "")
      .trim()
      .toLowerCase();

    return (
      normalizedStatus === "paid" ||
      normalizedStatus === "success" ||
      normalizedStatus === "successful" ||
      normalizedStatus === "completed"
    );
  }

  private getInvoiceMasterForStatus(
    invoiceId: number,
  ): Promise<SchoolInvoiceMasterDTO | null> {
    if (!invoiceId || invoiceId <= 0) {
      return Promise.resolve(null);
    }

    const cachedRequest = this.invoiceMasterLookupCache.get(invoiceId);

    if (cachedRequest) {
      return cachedRequest;
    }

    const request = lastValueFrom(
      this.schoolInvoiceMasterService.SchoolInvoiceMaster_ReadById({
        invoiceId,
      }),
    )
      .then((invoice) => invoice || null)
      .catch((error) => {
        console.error(
          `Failed to read school invoice master ${invoiceId}`,
          error,
        );
        return null;
      });

    this.invoiceMasterLookupCache.set(invoiceId, request);

    return request;
  }

 private async resolvePaidInvoiceDetails(
  details: any[] | null | undefined,
): Promise<any[]> {
  const detailItems = Array.isArray(details) ? details : [];

  const resolvedItems = await Promise.all(
    detailItems.map(async (detail: any) => {
      const invoiceId = Number(
        detail?.invoiceId ||
        detail?.schoolInvoiceId ||
        detail?.receiptId ||
        0
      );

      // Never trust detail status alone.
      if (!invoiceId || invoiceId <= 0) {
        return null;
      }

      const invoiceMaster = await this.getInvoiceMasterForStatus(invoiceId);

      // Only a server-confirmed paid parent invoice counts as payment.
      if (
        !invoiceMaster ||
        !this.isPaidStatus((invoiceMaster as any)?.status)
      ) {
        return null;
      }

      return {
        ...detail,
        pmtStatus: "Paid",
        prrn:
          detail?.prrn ||
          detail?.PRRN ||
          (invoiceMaster as any)?.prrn ||
          (invoiceMaster as any)?.PRRN ||
          "",
        invoiceNo:
          detail?.invoiceNo ||
          (invoiceMaster as any)?.invoiceNo ||
          (invoiceMaster as any)?.invoiceNumber ||
          "",
        invoiceNumber:
          detail?.invoiceNumber ||
          (invoiceMaster as any)?.invoiceNumber ||
          "",
        paymentMethod:
          detail?.paymentMethod ||
          detail?.otherDetail2 ||
          (invoiceMaster as any)?.paymentMode ||
          (invoiceMaster as any)?.otherDetail1 ||
          "",
        paymentDate:
          detail?.paymentDate ||
          detail?.otherDetail3 ||
          (invoiceMaster as any)?.modifiedOn ||
          (invoiceMaster as any)?.modifiedAt ||
          (invoiceMaster as any)?.invoiceDate ||
          detail?.modifiedOn ||
          detail?.createdOn,
        paidDate:
          detail?.paidDate ||
          (invoiceMaster as any)?.modifiedOn ||
          (invoiceMaster as any)?.modifiedAt ||
          (invoiceMaster as any)?.invoiceDate,
        invoiceStatus: (invoiceMaster as any)?.status || "Paid",
      };
    })
  );

  return resolvedItems
    .filter((item): item is any => !!item)
    .sort((a: any, b: any) => {
      const firstDate = this.getPaymentDateFromSource(a).getTime();
      const secondDate = this.getPaymentDateFromSource(b).getTime();

      if (firstDate !== secondDate) {
        return firstDate - secondDate;
      }

      return Number(a?.detailId || 0) - Number(b?.detailId || 0);
    });
}

  private waitForMilliseconds(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  private async reloadStudentFeeDetailAfterCollection(
    student: StudentClassMappingDTO,
    keepCollectingMode: boolean,
  ): Promise<void> {
    this.invoiceMasterLookupCache.clear();
    this.openStudentFeeDetail(student, keepCollectingMode);

    const startedAt = Date.now();
    const timeoutMs = 10000;

    while (Date.now() - startedAt < timeoutMs) {
      await this.waitForMilliseconds(200);

      const currentView = this.selectedStudentFeeView;

      if (
        !this.isFeeDetailLoading &&
        currentView &&
        currentView.student?.userId === student.userId &&
        !currentView.isLoading
      ) {
        this.loadOverviewFeeData();
        return;
      }
    }
    this.loadOverviewFeeData();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // School Resolution
  // ─────────────────────────────────────────────────────────────────────────

  private resolveSchoolId(): void {
    this.isSchoolLoading = true;

    const payload: UserAffiliationMappingReadByUserIdRequestDTO = {
      userId: this.User.userId,
    };

    this.affiliationService
      .UserAffiliationMapping_ReadByUserId(payload)
      .subscribe({
        next: (res) => {
          const items = res?.items || [];
          const schoolAffiliation =
            items.find(
              (x) =>
                x.schoolId &&
                x.schoolId > 0 &&
                x.affiliationType?.toLowerCase() !== "student",
            ) || items[0];

          if (schoolAffiliation) {
            this.schoolId = schoolAffiliation.schoolId;
            this.schoolName = (schoolAffiliation as any).schoolName || "";
            this.loadClasses();
            // Auto-load overview data on initial page load
            this.loadOverviewFeeData();
          } else {
            this.toastr.error("School not found for this user.");
          }

          this.isSchoolLoading = false;
        },
        error: () => {
          this.toastr.error("Failed to load school information.");
          this.isSchoolLoading = false;
        },
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Class Level
  // ─────────────────────────────────────────────────────────────────────────

  private loadClasses(): void {
    this.isClassLoading = true;

    const payload: ClassMasterReadBySchoolIdRequestDTO = {
      schoolId: this.schoolId,
      pageNo: 1,
      pageSize: 1000,
      whereClause: "",
      orderByClause: "",
    };

    this.classMasterService.ClassMaster_ReadAllPaginated(payload).subscribe({
      next: (res) => {
        this.classList = res.items || [];
        this.isClassLoading = false;

        if (this.preselectedClassId > 0) {
          const selectedClass = this.classList.find(
            (x) => x.classId === this.preselectedClassId,
          );

          if (selectedClass) {
            this.selectClass(selectedClass);
          }
        }
      },
      error: () => {
        this.toastr.error("Failed to load classes.");
        this.isClassLoading = false;
      },
    });
  }

  get filteredClasses(): ClassMasterDTO[] {
    if (!this.classSearchTerm.trim()) return this.classList;

    const term = this.classSearchTerm.toLowerCase();

    return this.classList.filter(
      (c) =>
        c.className?.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term),
    );
  }

  selectClass(classItem: ClassMasterDTO): void {
    if (!this.isRestoringState) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          classId: classItem.classId,
          sectionId: null,
          studentId: null,
        },
        queryParamsHandling: "merge",
      });
    }

    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
    this.selectedClass = classItem;
    this.sectionList = [];
    this.studentList = [];
    this.selectedSection = null;
    this.selectedStudentFeeView = null;
    this.isSectionLoading = true;
    this.currentLevel = "sections";

    this.sectionMasterService
      .SectionMaster_ReadByClassId({ classId: classItem.classId })
      .subscribe({
        next: (res) => {
          this.sectionList = res.items || [];
          this.isSectionLoading = false;

          if (this.sectionList.length === 0) {
            this.noSectionsFallback = true;
            this.currentLevel = "sections";
            this.loadStudents(classItem.classId, null);
          } else {
            this.noSectionsFallback = false;
            this.currentLevel = "sections";

            if (this.preselectedSectionId > 0) {
              const selectedSection = this.sectionList.find(
                (x) => x.sectionId === this.preselectedSectionId,
              );

              if (selectedSection) {
                this.selectSection(selectedSection);
              }
            }
          }
        },
        error: () => {
          this.toastr.error("Failed to load sections.");
          this.isSectionLoading = false;
        },
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Section Level
  // ─────────────────────────────────────────────────────────────────────────

  selectSection(section: SectionMasterDTO | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        classId: this.selectedClass?.classId,
        sectionId: section?.sectionId || null,
        studentId: null,
      },
      queryParamsHandling: "merge",
});

    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();
    this.selectedSection = section;

    const sectionId = section ? section.sectionId : null;
    this.loadStudents(this.selectedClass!.classId, sectionId);
  }

  selectAllStudents(): void {
    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();
    this.selectedSection = null;
    this.loadStudents(this.selectedClass!.classId, null);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Student Level
  // ─────────────────────────────────────────────────────────────────────────

  private loadStudents(classId: number, sectionId: number | null): void {
    this.isStudentLoading = true;
    this.studentList = [];
    this.selectedStudentFeeView = null;
    this.currentLevel = "students";

    const payload: StudentClassMappingReadByClassIdRequestDTO = {
      classId,
      sectionId: sectionId ?? null,
      pageNo: 1,
      pageSize: 1000,
      whereClause: "",
      orderByClause: "",
    };

    this.studentClassMappingService
      .StudentClassMapping_ReadByClassId(payload)
      .subscribe({
        next: (res) => {
          this.studentList = res.items || [];
          this.isStudentLoading = false;

          this.markAllStudentsPending();

          if (this.preselectedStudentId > 0) {
            const student = this.studentList.find(
              (s) => s.userId === this.preselectedStudentId,
            );

            if (student) {
              this.openStudentFeeDetail(student, false);
            }
          }
        },
        error: () => {
          this.toastr.error("Failed to load students.");
          this.isStudentLoading = false;
        },
      });
  }

  private markAllStudentsPending(): void {
    this.pendingFeeStudentIds = new Set(
      this.studentList
        .filter((s) => s.userId && s.userId > 0)
        .map((s) => s.userId!),
    );
  }

  studentHasPendingFee(student: StudentClassMappingDTO): boolean {
    return this.pendingFeeStudentIds.has(student.userId ?? 0);
  }

  get filteredStudents(): StudentClassMappingDTO[] {
    if (!this.studentSearchTerm.trim()) return this.studentList;

    const term = this.studentSearchTerm.toLowerCase();

    return this.studentList.filter((s) => {
      const name = `${s.firstName || ""} ${s.middleName || ""} ${s.lastName || ""}`.toLowerCase();

      return (
        name.includes(term) ||
        s.emailId?.toLowerCase().includes(term) ||
        s.mobileNo?.includes(term)
      );
    });
  }

  getStudentInitials(student: StudentClassMappingDTO): string {
    const f = student.firstName?.[0] || "";
    const l = student.lastName?.[0] || "";
    return (f + l).toUpperCase() || "S";
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fee Detail Level
  // ─────────────────────────────────────────────────────────────────────────

  openStudentFeeDetail(
    student: StudentClassMappingDTO,
    isCollectingMode: boolean = false,
  ): void {
    this.isCollectingMode = isCollectingMode;
    this.selectedFeeDisplayTab = "mandatory";
    this.expandedMandatoryInstallmentNumbers.clear();
    this.expandedInstallmentDetailIds.clear();
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        classId: this.selectedClass?.classId,
        sectionId: this.selectedSection?.sectionId,
        studentId: student.userId,
      },
      queryParamsHandling: "merge",
    });

    this.selectedStudentFeeView = null;
    this.currentLevel = "feeDetail";
    this.isFeeDetailLoading = true;

    const studentId = student.userId ?? 0;

    this.studentFeeStructureMappingService
      .StudentFeeStructureMapping_ReadByStudentId({ studentId })
      .subscribe({
        next: (mappingRes) => {
          const mappedIds = (mappingRes.items || [])
            .map((m) => m.feeStructureId)
            .filter(Boolean) as number[];

          if (!mappedIds.length) {
            this.selectedStudentFeeView = this.buildEmptyFeeView(student);
            this.pendingFeeStudentIds.delete(student.userId ?? 0);
            this.isFeeDetailLoading = false;
            return;
          }

          this.feeStructureService
            .FeeStructureMaster_ReadFeesDetailsByClassId({
              classId: this.selectedClass!.classId,
            })
            .subscribe({
              next: (res: FeesMasterDetailsDTO) => {
                this.selectedStudentFeeView = this.buildFeeView(
                  student,
                  res,
                  mappedIds,
                );

                if (this.isCollectingMode) {
                  this.expandAllFeesForCollection(this.selectedStudentFeeView);
                }

                this.isFeeDetailLoading = false;
              },
              error: () => {
                this.toastr.error("Failed to load fee details.");
                this.isFeeDetailLoading = false;
              },
            });
        },
        error: () => {
          this.toastr.error("Failed to load fee mapping.");
          this.isFeeDetailLoading = false;
        },
      });
  }

  openFeeCollecting(student: StudentClassMappingDTO): void {
    this.openStudentFeeDetail(student, true);
  }
  
  // ============================================================
  // OPEN SCHOOL PAID AMOUNT EDIT MODAL
  // ============================================================

  async openEditPaidAmountModal(
    view: StudentFeeView,
    modalTemplate: TemplateRef<any>
  ): Promise<void> {

    if (!view?.student?.userId) {
      this.toastr.error("Student information not found.");
      return;
    }

    this.paidEditStudentView = view;
    this.paidEditRows = [];
    this.paidEditError = "";
    this.isPaidEditLoading = true;

    this.modalService.open(modalTemplate, {
      size: "xl",
      centered: true,
      backdrop: "static",
      scrollable: true
    });

    try {

      const studentId = Number(view.student.userId);

      const requests: Array<{
        feeName: string;
        installmentName: string;
        masterId: number;
        masterType: string;
      }> = [];

      for (const fee of view.feeStructureList) {

        if (fee.installments && fee.installments.length > 0) {

          for (const installment of fee.installments) {

            if (!installment.installmentId) {
              continue;
            }

            requests.push({
              feeName: fee.fName || "Fee",
              installmentName:
                installment.installmentName || "Installment",
              masterId: Number(installment.installmentId),
              masterType: "Installment"
            });

          }

        } else if (fee.feeStructureId) {

          requests.push({
            feeName: fee.fName || "Fee",
            installmentName: "One-time Fee",
            masterId: Number(fee.feeStructureId),
            masterType: "FEE_DETAIL"
          });

        }

      }

      const results = await Promise.all(

        requests.map(async (request) => {

          const response = await lastValueFrom(

            this.schoolInvoiceDetailService
              .SchoolInvoiceDetail_ReadByMasterId({
                billedTo: studentId,
                masterId: request.masterId,
                masterType: request.masterType
              })

          );

          const paidItems = await this.resolvePaidInvoiceDetails(
            response?.items || []
          );

          return {
            request,
            paidItems
          };

        })

      );

      const rows: SchoolEditablePaidRow[] = [];

      for (const result of results) {

        for (const item of result.paidItems) {

          const detailId = Number(item.detailId || 0);

          const invoiceId = Number(item.invoiceId || 0);

          const amount = Number(item.amount || 0);

          if (
            detailId <= 0 ||
            invoiceId <= 0 ||
            amount <= 0
          ) {
            continue;
          }

          rows.push({

            detailId,
            invoiceId,

            masterId: result.request.masterId,
            masterType: result.request.masterType,

            feeName: result.request.feeName,
            installmentName: result.request.installmentName,

            paymentDate: this.getPaymentDateFromSource(item),

            originalAmount: amount,
            newAmount: amount,

            source: { ...item }

          });

        }

      }

      this.paidEditRows = rows.sort((a, b) => {

        return (
          a.paymentDate.getTime() -
          b.paymentDate.getTime()
        );

      });

    } catch (error) {

      console.error(
        "School paid amount listing error:",
        error
      );

      this.paidEditError =
        "Unable to load payment history.";

    } finally {

      this.isPaidEditLoading = false;

    }

  }


  // ============================================================
  // CURRENT PAID TOTAL
  // ============================================================

  get paidEditCurrentTotal(): number {

    return Number(

      this.paidEditRows.reduce(
        (total, row) =>
          total + Number(row.originalAmount || 0),
        0
      ).toFixed(2)

    );

  }


  // ============================================================
  // PROPOSED PAID TOTAL
  // ============================================================

  get paidEditProposedTotal(): number {

    return Number(

      this.paidEditRows.reduce(
        (total, row) =>
          total + Number(row.newAmount || 0),
        0
      ).toFixed(2)

    );

  }


  // ============================================================
  // VALIDATE SCHOOL PAYMENT CORRECTION
  // ============================================================

 
  // ============================================================
  // UPDATE SCHOOL PAID AMOUNT
  // ============================================================

  async saveEditedSchoolPayment(
    row: SchoolEditablePaidRow
  ): Promise<void> {

    this.paidEditError = "";

    if (this.isPaidEditSaving) {
      return;
    }

    if (
      !row ||
      row.detailId <= 0 ||
      row.invoiceId <= 0
    ) {
      this.paidEditError = "Invalid payment transaction.";
      return;
    }

    if (
      row.newAmount === null ||
      row.newAmount === undefined ||
      String(row.newAmount).trim() === ""
    ) {
      this.paidEditError = "Enter the new paid amount.";
      return;
    }

    const newAmount = Number(row.newAmount);

    if (
      !Number.isFinite(newAmount) ||
      newAmount <= 0 ||
      !/^\d+(\.\d{1,2})?$/.test(String(row.newAmount))
    ) {
      this.paidEditError =
        "Enter a valid positive amount with maximum two decimal places.";
      return;
    }

    if (
      Math.round(newAmount * 100) ===
      Math.round(row.originalAmount * 100)
    ) {
      return;
    }

    if (!this.User?.userId) {
      this.paidEditError = "Logged-in user not found.";
      return;
    }

    const source = row.source;

    if (!source) {
      this.paidEditError = "Original payment details not found.";
      return;
    }

    // Confirm the financial correction before submitting.
    const confirmed = window.confirm(
      `Change paid amount from ₹${row.originalAmount.toFixed(2)} ` +
      `to ₹${newAmount.toFixed(2)}?\n\n` +
      `Invoice ID: ${row.invoiceId}\n` +
      `Detail ID: ${row.detailId}\n\n` +
      `This modifies an existing payment record.`
    );

    if (!confirmed) {
      return;
    }

    const payload: SchoolInvoiceDetailUpdateRequestDTO = {

      detailId: row.detailId,

      invoiceId: row.invoiceId,

      masterType: row.masterType,

      masterId: row.masterId,

      amount: Number(newAmount.toFixed(2)),

      remarks: source.remarks ?? "",

      taxType: source.taxType ?? "",

      taxAmount: Number(source.taxAmount ?? 0),

      discountType: source.discountType ?? "",

      discountAmount: Number(source.discountAmount ?? 0),

      baseAmount: Number(source.baseAmount ?? row.originalAmount),

      pmtStatus: source.pmtStatus ?? "Paid",

      description: source.description ?? "",

      otherDetail1: source.otherDetail1 ?? "",

      otherDetail2: source.otherDetail2 ?? "",

      otherDetail3: source.otherDetail3 ?? "",

      otherDetail4: source.otherDetail4 ?? "",

      otherDetail5: source.otherDetail5 ?? "",

      otherDetail6: source.otherDetail6 ?? "",

      isActive: Number(source.isActive ?? 1),

      actionUser: String(this.User.userId)

    };

    this.isPaidEditSaving = true;

    try {

      // STEP 1 - Call existing Update API.
      await lastValueFrom(
        this.schoolInvoiceDetailService
          .SchoolInvoiceDetail_Update(payload)
      );

      // STEP 2 - Read the record again from backend.
      const verifiedRecord = await lastValueFrom(
        this.schoolInvoiceDetailService
          .SchoolInvoiceDetail_ReadById({
            detailId: row.detailId
          })
      );

      if (
        !verifiedRecord ||
        Number(verifiedRecord.detailId) !== row.detailId ||
        Number(verifiedRecord.invoiceId) !== row.invoiceId ||
        Math.round(Number(verifiedRecord.amount) * 100) !==
          Math.round(newAmount * 100)
      ) {
        throw new Error(
          "Update response could not be verified. Refresh before retrying."
        );
      }

      // STEP 3 - Update popup only after verified persistence.
      row.originalAmount = Number(verifiedRecord.amount);

      row.newAmount = Number(verifiedRecord.amount);

      row.source = { ...verifiedRecord };

      // STEP 4 - Clear invoice status cache.
      this.invoiceMasterLookupCache.clear();

      // STEP 5 - Reload student fee summary.
      const student = this.paidEditStudentView?.student;

      if (student) {

        await this.reloadStudentFeeDetailAfterCollection(
          student,
          this.isCollectingMode
        );

        this.paidEditStudentView = this.selectedStudentFeeView;

      }

      this.toastr.success(
        "Paid amount updated successfully."
      );

    } catch (error: any) {

      console.error(
        "School paid amount update failed:",
        error
      );

      this.paidEditError =
        error?.error?.message ||
        error?.message ||
        "Unable to verify payment update. Refresh and check the transaction before retrying.";

    } finally {

      this.isPaidEditSaving = false;

    }

  }


  private expandAllFeesForCollection(view: StudentFeeView): void {
    view.expandedFeeIds = new Set(
      view.feeStructureList
        .filter((fee) => fee.feeStructureId)
        .map((fee) => fee.feeStructureId),
    );
  }

  private buildEmptyFeeView(student: StudentClassMappingDTO): StudentFeeView {
    return {
      student,
      feeGroups: [],
      mandatoryInstallmentRows: [],
      feeStructureList: [],
      installmentPaymentStatus: new Map(),
      oneTimeFeePaymentStatus: new Map(),
      isLoading: false,
      noFeeAssigned: true,
      expandedFeeIds: new Set(),
      grandTotal: 0,
      grandPaid: 0,
      grandDue: 0,
      grandLateFees: 0,
      progressPercent: 0,
    };
  }

  private buildFeeView(
    student: StudentClassMappingDTO,
    res: FeesMasterDetailsDTO,
    mappedIds: number[],
  ): StudentFeeView {
    const feeStructures = res.feeStructureMasterList || [];
    const installments = res.feeInstallmentMasterList || [];
    const components = res.feeComponentMasterList || [];

    const filtered = feeStructures.filter((f) =>
      mappedIds.includes(f.feeStructureId),
    );

    const compByInstId: { [id: number]: FeeComponentMasterDTO[] } = {};

    for (const comp of components) {
      if (comp.installmentId != null) {
        compByInstId[comp.installmentId] =
          compByInstId[comp.installmentId] || [];
        compByInstId[comp.installmentId].push(comp);
      }
    }

    const instByFeeId: { [id: number]: FeeInstallmentMasterDTO[] } = {};
    const allInstIds: number[] = [];

    for (const inst of installments) {
      if (inst.installmentId != null && inst.feeStructureId != null) {
        inst.components = compByInstId[inst.installmentId] || [];
        instByFeeId[inst.feeStructureId] =
          instByFeeId[inst.feeStructureId] || [];
        instByFeeId[inst.feeStructureId].push(inst);

        if (mappedIds.includes(inst.feeStructureId)) {
          allInstIds.push(inst.installmentId);
        }
      }
    }

    const feeStructureList: FeeStructureMasterDTO[] = [];
    const groupMap: { [feeType: string]: FeeGroup } = {};

    for (const fee of filtered) {
      fee.installments = instByFeeId[fee.feeStructureId] || [];
      feeStructureList.push(fee);

      // Mandatory fees are rendered as one installment schedule below.
      // Keep each original fee in feeStructureList for invoices and payment status.
      if (Number(fee.isMandatory || 0) !== 1) {
        const fType = fee.feeType || "Other";

        if (!groupMap[fType]) {
          groupMap[fType] = { feeType: fType, fees: [] };
        }

        groupMap[fType].fees.push(fee);
      }
    }

    this.invoiceMasterLookupCache.clear();

    const view: StudentFeeView = {
      student,
      feeGroups: Object.values(groupMap),
      mandatoryInstallmentRows: [],
      feeStructureList,
      installmentPaymentStatus: new Map(),
      oneTimeFeePaymentStatus: new Map(),
      isLoading: true,
      noFeeAssigned: false,
      expandedFeeIds: new Set(),
      grandTotal: feeStructureList.reduce((s, f) => s + (f.amount || 0), 0),
      grandPaid: 0,
      grandDue: 0,
      grandLateFees: 0,
      progressPercent: 0,
    };

    const oneTimeFeeStructureIds = feeStructureList
      .filter((fee) => !fee.installments || fee.installments.length === 0)
      .map((fee) => fee.feeStructureId)
      .filter((id): id is number => !!id);

    this.fetchPaymentStatuses(view, allInstIds);
    this.fetchOneTimeFeePaymentStatuses(view, oneTimeFeeStructureIds);

    return view;
  }

  private fetchPaymentStatuses(
    view: StudentFeeView,
    installmentIds: number[],
  ): void {
    if (!installmentIds.length) {
      view.isLoading = false;
      this.recalcViewTotals(view);
      this.updateStudentPendingStatus(view);
      return;
    }

    const LATE_FEE_PCT = 2;

    const requests = installmentIds.map((id) =>
      this.schoolInvoiceDetailService
        .SchoolInvoiceDetail_ReadByMasterId({
          billedTo: view.student.userId ?? 0,
          masterId: id,
          masterType: "Installment",
        })
        .toPromise(),
    );

    Promise.all(requests)
      .then(async (results) => {
        const paidItemsByInstallment = await Promise.all(
          results.map((result) =>
            this.resolvePaidInvoiceDetails(result?.items || []),
          ),
        );

        results.forEach((_result, idx) => {
          const installmentId = installmentIds[idx];
          const installment = this.findInstallmentByIdInView(
            view,
            installmentId,
          );
          const installmentAmount = installment?.amount || 0;

          const paidItems = paidItemsByInstallment[idx] || [];

          const totalPaid = paidItems.reduce(
            (s: number, d: any) => s + (d.amount || 0),
            0,
          );

          const remaining = Math.max(0, installmentAmount - totalPaid);
          const isPaid =
            totalPaid >= installmentAmount && installmentAmount > 0;

          if (installment && totalPaid > 0) {
            const fee = this.findFeeByInstallmentIdInView(view, installmentId);
            const paymentHistory = this.buildPaymentHistoryFromPaidItems(paidItems);
            const latestPaidItem = paidItems[paidItems.length - 1];
            const latestHistory = paymentHistory[paymentHistory.length - 1];
            const billStatus = remaining <= 0 ? "Paid" : "Partial";

            if (fee) {
              const bill = this.buildFeeCollectionBill(
                view,
                fee,
                installment,
                latestPaidItem,
                totalPaid,
                billStatus,
                latestHistory?.paymentMethod ||
                  (latestPaidItem as any)?.otherDetail2 ||
                  (latestPaidItem as any)?.paymentMethod ||
                  "Cash",
                paymentHistory,
              );
              this.feeCollectionBillByInstallmentId.set(installmentId, bill);
              const key = bill.receiptNo || String(bill.invoiceId || "");
              if (key) {
                this.sessionReceiptsRegistry.set(key, bill);
              }
            }
          }

          const isOverdueFlag = installment?.paymentDueDate
            ? new Date() > new Date(installment.paymentDueDate)
            : false;

          const lateFee =
            !isPaid && isOverdueFlag ? (remaining * LATE_FEE_PCT) / 100 : 0;

          view.installmentPaymentStatus.set(installmentId, {
            paidAmount: totalPaid,
            isPaid,
            remainingAmount: remaining,
            lateFeeAmount: lateFee,
            totalAmount: remaining + lateFee,
          });
        });

        view.isLoading = false;
        this.recalcViewTotals(view);
        this.updateStudentPendingStatus(view);

        if (this.isCollectingMode) {
          this.expandAllFeesForCollection(view);
        }
      })
      .catch(() => {
        installmentIds.forEach((id) => {
          const inst = this.findInstallmentByIdInView(view, id);
          const amt = inst?.amount || 0;

          const overdue = inst?.paymentDueDate
            ? new Date() > new Date(inst.paymentDueDate)
            : false;

          const lateFee = overdue ? (amt * LATE_FEE_PCT) / 100 : 0;

          view.installmentPaymentStatus.set(id, {
            paidAmount: 0,
            isPaid: false,
            remainingAmount: amt,
            lateFeeAmount: lateFee,
            totalAmount: amt + lateFee,
          });
        });

        view.isLoading = false;
        this.recalcViewTotals(view);
        this.updateStudentPendingStatus(view);

        if (this.isCollectingMode) {
          this.expandAllFeesForCollection(view);
        }
      });
  }

  private fetchOneTimeFeePaymentStatuses(
    view: StudentFeeView,
    feeStructureIds: number[],
  ): void {
    if (!feeStructureIds || feeStructureIds.length === 0) {
      this.recalcViewTotals(view);
      this.updateStudentPendingStatus(view);
      return;
    }

    const requests = feeStructureIds.map((feeStructureId) =>
      this.schoolInvoiceDetailService
        .SchoolInvoiceDetail_ReadByMasterId({
          billedTo: view.student.userId ?? 0,
          masterId: feeStructureId,
          masterType: "FEE_DETAIL",
        })
        .toPromise(),
    );

    Promise.all(requests)
      .then(async (results) => {
        const paidItemsByFeeStructure = await Promise.all(
          results.map((result) =>
            this.resolvePaidInvoiceDetails(result?.items || []),
          ),
        );

        results.forEach((_result, index) => {
          const feeStructureId = feeStructureIds[index];

          const fee = view.feeStructureList.find(
            (item) => Number(item.feeStructureId) === Number(feeStructureId),
          );

          const feeAmount = Number(fee?.amount || 0);

          const paidItems = paidItemsByFeeStructure[index] || [];

          const totalPaid = paidItems.reduce(
            (sum: number, detail: any) => sum + Number(detail.amount || 0),
            0,
          );

          const remainingAmount = Math.max(0, feeAmount - totalPaid);
          const isPaid = totalPaid >= feeAmount && feeAmount > 0;
          const latestPaidItem = paidItems[paidItems.length - 1];

          const paymentHistory = this.buildPaymentHistoryFromPaidItems(paidItems);
          const latestHistory = paymentHistory[paymentHistory.length - 1];

          view.oneTimeFeePaymentStatus.set(feeStructureId, {
            paidAmount: Number(totalPaid.toFixed(2)),
            isPaid,
            remainingAmount: Number(remainingAmount.toFixed(2)),
            lateFeeAmount: 0,
            totalAmount: Number(remainingAmount.toFixed(2)),
            paymentMethod:
              latestHistory?.paymentMethod ||
              (latestPaidItem as any)?.otherDetail2 ||
              (latestPaidItem as any)?.paymentMethod ||
              "Online",
            latestPaymentDate:
              latestHistory?.paymentDate || this.getPaymentDateFromSource(latestPaidItem),
            latestPaidItem,
          });

          if (fee && totalPaid > 0) {
            const fakeInstallment = this.createOneTimeFakeInstallment(fee);
            const billStatus = remainingAmount <= 0 ? "Paid" : "Partial";

            const bill = this.buildFeeCollectionBill(
              view,
              fee,
              fakeInstallment,
              latestPaidItem,
              totalPaid,
              billStatus,
              latestHistory?.paymentMethod ||
                (latestPaidItem as any)?.otherDetail2 ||
                (latestPaidItem as any)?.paymentMethod ||
                "Online",
              paymentHistory,
            );

            this.feeCollectionBillByFeeStructureId.set(feeStructureId, bill);
            const key = bill.receiptNo || String(bill.invoiceId || "");
            if (key) {
              this.sessionReceiptsRegistry.set(key, bill);
            }
          } else {
            this.feeCollectionBillByFeeStructureId.delete(feeStructureId);
          }
        });

        view.isLoading = false;
        this.recalcViewTotals(view);
        this.updateStudentPendingStatus(view);

        if (this.isCollectingMode) {
          this.expandAllFeesForCollection(view);
        }
      })
      .catch(() => {
        feeStructureIds.forEach((feeStructureId) => {
          const fee = view.feeStructureList.find(
            (item) => Number(item.feeStructureId) === Number(feeStructureId),
          );

          const feeAmount = Number(fee?.amount || 0);

          view.oneTimeFeePaymentStatus.set(feeStructureId, {
            paidAmount: 0,
            isPaid: false,
            remainingAmount: feeAmount,
            lateFeeAmount: 0,
            totalAmount: feeAmount,
          });

          this.feeCollectionBillByFeeStructureId.delete(feeStructureId);
        });

        view.isLoading = false;
        this.recalcViewTotals(view);
        this.updateStudentPendingStatus(view);

        if (this.isCollectingMode) {
          this.expandAllFeesForCollection(view);
        }
      });
  }

  private recalcViewTotals(view: StudentFeeView): void {
    let paid = 0;
    let due = 0;
    let late = 0;

    for (const fee of view.feeStructureList) {
      if (!fee.installments || fee.installments.length === 0) {
        const feeStructureId = fee.feeStructureId || 0;
        const status = view.oneTimeFeePaymentStatus.get(feeStructureId);

        paid += status?.paidAmount || 0;
        due += status ? status.remainingAmount || 0 : Number(fee.amount || 0);
        late += 0;

        continue;
      }

      for (const inst of fee.installments || []) {
        const status = view.installmentPaymentStatus.get(inst.installmentId!);
        paid += status?.paidAmount || 0;
        due += status?.remainingAmount || 0;
        late += status?.lateFeeAmount || 0;
      }
    }

    view.grandPaid = Number(paid.toFixed(2));
    view.grandDue = Number(due.toFixed(2));
    view.grandLateFees = Number(late.toFixed(2));
    view.mandatoryInstallmentRows = this.buildMandatoryCombinedInstallments(view);
    view.progressPercent = view.grandTotal
      ? Math.min(100, Math.round((paid / view.grandTotal) * 100))
      : 0;
  }

  private updateStudentPendingStatus(view: StudentFeeView): void {
    const uid = view.student.userId ?? 0;

if (!uid) return;

    if (view.grandDue <= 0) {
      this.pendingFeeStudentIds.delete(uid);
    } else {
      this.pendingFeeStudentIds.add(uid);
    }
  }

  private findInstallmentByIdInView(
    view: StudentFeeView,
    id: number,
  ): FeeInstallmentMasterDTO | undefined {
    for (const fee of view.feeStructureList) {
      const inst = fee.installments?.find((i) => i.installmentId === id);
      if (inst) return inst;
    }

    return undefined;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fee View Helpers
  // ─────────────────────────────────────────────────────────────────────────

  toggleFeeExpand(view: StudentFeeView, feeStructureId: number): void {
    view.expandedFeeIds.has(feeStructureId)
      ? view.expandedFeeIds.delete(feeStructureId)
      : view.expandedFeeIds.add(feeStructureId);
  }

  isFeeFullyPaid(view: StudentFeeView, fee: FeeStructureMasterDTO): boolean {
    if (!fee.installments?.length) {
      const status = view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0);
      return status?.isPaid || false;
    }

    return fee.installments.every(
      (i) => view.installmentPaymentStatus.get(i.installmentId!)?.isPaid,
    );
  }

  hasFeeOverdue(view: StudentFeeView, fee: FeeStructureMasterDTO): boolean {
    if (!fee.installments?.length) return false;

    if (this.isFeeFullyPaid(view, fee)) return false;

    return (fee.installments || []).some(
      (i) =>
        !view.installmentPaymentStatus.get(i.installmentId!)?.isPaid &&
        (view.installmentPaymentStatus.get(i.installmentId!)?.lateFeeAmount ||
          0) > 0,
    );
  }

  getFeePaid(view: StudentFeeView, fee: FeeStructureMasterDTO): number {
    if (!fee.installments?.length) {
      return view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0)?.paidAmount || 0;
    }

    return (fee.installments || []).reduce(
      (s, i) =>
        s +
        (view.installmentPaymentStatus.get(i.installmentId!)?.paidAmount || 0),
      0,
    );
  }

  getFeePending(view: StudentFeeView, fee: FeeStructureMasterDTO): number {
    if (!fee.installments?.length) {
      const status = view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0);

      if (status) {
        return status.remainingAmount || 0;
      }

      return Number(fee.amount || 0);
    }

    return (fee.installments || []).reduce(
      (s, i) =>
        s +
        (view.installmentPaymentStatus.get(i.installmentId!)?.remainingAmount ||
          0),
      0,
    );
  }

  getFeeLateFees(view: StudentFeeView, fee: FeeStructureMasterDTO): number {
    if (!fee.installments?.length) return 0;

    return (fee.installments || []).reduce(
      (s, i) =>
        s +
        (view.installmentPaymentStatus.get(i.installmentId!)?.lateFeeAmount ||
          0),
      0,
    );
  }

  getFeeProgressPercent(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    if (!fee.amount) return 0;

    return Math.min(
      100,
      Math.round((this.getFeePaid(view, fee) / fee.amount) * 100),
    );
  }

  getRingOffset(view: StudentFeeView, fee: FeeStructureMasterDTO): number {
    const circumference = 113;

    return (
      circumference -
      circumference * (this.getFeeProgressPercent(view, fee) / 100)
    );
  }

  getFeeStatus(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    if (this.isFeeFullyPaid(view, fee)) return "Paid";
    if (this.hasFeeOverdue(view, fee)) return "Overdue";
    if (this.getFeePaid(view, fee) > 0) return "Partial";
    return "Pending";
  }

  getFeeStatusChipClass(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): string {
    return (
      (
        {
          Paid: "chip-paid",
          Overdue: "chip-overdue",
          Partial: "chip-partial",
          Pending: "chip-pending",
        } as any
      )[this.getFeeStatus(view, fee)] ?? "chip-pending"
    );
  }

  getFeeStatusIcon(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    return (
      (
        {
          Paid: "bi-check-circle-fill",
          Overdue: "bi-exclamation-triangle-fill",
          Partial: "bi-slash-circle",
          Pending: "bi-hourglass-split",
        } as any
      )[this.getFeeStatus(view, fee)] ?? "bi-hourglass-split"
    );
  }

  getFeeIconClass(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    return (
      (
        {
          Paid: "fc-icon-paid",
          Overdue: "fc-icon-overdue",
          Partial: "fc-icon-partial",
          Pending: "fc-icon-pending",
        } as any
      )[this.getFeeStatus(view, fee)] ?? "fc-icon-pending"
    );
  }

  getFeeIcon(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    return (
      (
        {
          Paid: "bi-check-all",
          Overdue: "bi-exclamation-triangle",
          Partial: "bi-clock-history",
          Pending: "bi-hourglass-split",
        } as any
      )[this.getFeeStatus(view, fee)] ?? "bi-hourglass-split"
    );
  }

  isInstallmentPaid(view: StudentFeeView, id: number): boolean {
    return view.installmentPaymentStatus.get(id)?.isPaid || false;
  }

  getInstallmentPaidAmount(view: StudentFeeView, id: number): number {
    return view.installmentPaymentStatus.get(id)?.paidAmount || 0;
  }

  getInstallmentRemainingAmount(view: StudentFeeView, id: number): number {
    return view.installmentPaymentStatus.get(id)?.remainingAmount || 0;
  }

  getInstallmentLateFee(view: StudentFeeView, id: number): number {
    return view.installmentPaymentStatus.get(id)?.lateFeeAmount || 0;
  }

  isOverdue(date: any): boolean {
    if (!date) return false;
    return new Date() > new Date(date);
  }

  getInstRowClass(view: StudentFeeView, inst: FeeInstallmentMasterDTO): string {
    if (this.isInstallmentPaid(view, inst.installmentId!)) {
      return "inst-row-paid";
    }

    if (this.getInstallmentLateFee(view, inst.installmentId!) > 0) {
      return "inst-row-overdue";
    }

    return "";
  }

  getInstBadgeClass(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
  ): string {
    if (this.isInstallmentPaid(view, inst.installmentId!)) return "badge-paid";

    if (this.getInstallmentLateFee(view, inst.installmentId!) > 0) {
      return "badge-overdue";
    }

    return "badge-pending";
  }

  getInstBadgeIcon(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
  ): string {
    if (this.isInstallmentPaid(view, inst.installmentId!)) {
      return "bi-check-circle-fill";
    }

    if (this.getInstallmentLateFee(view, inst.installmentId!) > 0) {
      return "bi-exclamation-triangle-fill";
    }

    return "bi-hourglass";
  }

  getInstStatusLabel(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
  ): string {
    if (this.isInstallmentPaid(view, inst.installmentId!)) return "Paid";

    if (this.getInstallmentLateFee(view, inst.installmentId!) > 0) {
      return "Overdue";
    }

    return "Pending";
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fee Collection Helpers
  // ─────────────────────────────────────────────────────────────────────────

  private isOneTimeFee(fee: FeeStructureMasterDTO): boolean {
    return !fee.installments || fee.installments.length === 0;
  }

  private getOneTimeFeeSelectionKey(fee: FeeStructureMasterDTO): number {
    return -Math.abs(Number(fee.feeStructureId || 0));
  }

  private createOneTimeFakeInstallment(fee: FeeStructureMasterDTO): FeeInstallmentMasterDTO {
    return {
      installmentId: fee.feeStructureId,
      feeStructureId: fee.feeStructureId,
      installmentName: "One-time Fee",
      amount: Number(fee.amount || 0),
    } as FeeInstallmentMasterDTO;
  }

  canCollectOneTimeFee(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): boolean {
    if (!this.isOneTimeFee(fee)) return false;
    if (!fee.feeStructureId) return false;

    return !this.isFeeFullyPaid(view, fee) && this.getFeePending(view, fee) > 0;
  }

  isOneTimeFeeSelected(fee: FeeStructureMasterDTO): boolean {
    if (!fee.feeStructureId) return false;
    return this.selectedCollectionInstallmentIds.has(this.getOneTimeFeeSelectionKey(fee));
  }

  toggleCollectionOneTimeFee(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    event: Event,
  ): void {
    event.stopPropagation();

    const checkbox = event.target as HTMLInputElement;

    if (!this.canCollectOneTimeFee(view, fee)) {
      checkbox.checked = false;
      return;
    }

    const key = this.getOneTimeFeeSelectionKey(fee);

    if (this.selectedCollectionInstallmentIds.has(key)) {
      this.selectedCollectionInstallmentIds.delete(key);
    } else {
      this.selectedCollectionInstallmentIds.add(key);
    }

    this.resetCustomCollectionAmountState();
  }

  collectOneTimeFee(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    modalTemplate?: any,
  ): void {
    if (!this.canCollectOneTimeFee(view, fee)) {
      this.toastr.warning("This fee is already paid or not payable.", "Warning");
      return;
    }

    this.selectedCollectionInstallmentIds.clear();
    this.selectedCollectionInstallmentIds.add(this.getOneTimeFeeSelectionKey(fee));

    this.setCustomCollectionAmountValue(
      this.getFeePending(view, fee),
      true,
    );

    if (modalTemplate) {
      this.openCollectFeeModal(view, fee, modalTemplate);
      return;
    }

    this.proceedToCollectPayment(view, fee);
  }

  canCollectInstallment(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
  ): boolean {
    if (!inst.installmentId) return false;

    const isPaid = this.isInstallmentPaid(view, inst.installmentId);
    const remaining = this.getInstallmentRemainingAmount(
      view,
      inst.installmentId,
    );
    const lateFee = this.getInstallmentLateFee(view, inst.installmentId);

    return !isPaid && remaining + lateFee > 0;
  }

  isInstallmentSelected(instId: number): boolean {
    return this.selectedCollectionInstallmentIds.has(instId);
  }

  private getPayableInstallmentsForFee(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): FeeInstallmentMasterDTO[] {
    return (fee.installments || []).filter((item) =>
      item.installmentId && this.canCollectInstallment(view, item),
    );
  }

  private getRequiredPreviousInstallmentForSelection(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    inst: FeeInstallmentMasterDTO,
  ): FeeInstallmentMasterDTO | null {
    if (!inst.installmentId) {
      return null;
    }

    const payableInstallments = this.getPayableInstallmentsForFee(view, fee);
    const selectedIndex = payableInstallments.findIndex(
      (item) => item.installmentId === inst.installmentId,
    );

    if (selectedIndex <= 0) {
      return null;
    }

    for (let index = 0; index < selectedIndex; index++) {
      const previous = payableInstallments[index];

      if (
        previous.installmentId &&
        !this.selectedCollectionInstallmentIds.has(previous.installmentId)
      ) {
        return previous;
      }
    }

    return null;
  }

  private removeLaterSelectedInstallments(
    fee: FeeStructureMasterDTO,
    inst: FeeInstallmentMasterDTO,
  ): void {
    if (!inst.installmentId) {
      return;
    }

    const allInstallments = fee.installments || [];
    const selectedIndex = allInstallments.findIndex(
      (item) => item.installmentId === inst.installmentId,
    );

    if (selectedIndex < 0) {
      return;
    }

    for (let index = selectedIndex + 1; index < allInstallments.length; index++) {
      const nextInstallmentId = allInstallments[index]?.installmentId;

      if (nextInstallmentId) {
        this.selectedCollectionInstallmentIds.delete(nextInstallmentId);
      }
    }
  }

  private showInstallmentSequenceWarning(inst: FeeInstallmentMasterDTO): void {
    const installmentName = inst.installmentName || "previous installment";

    this.toastr.warning(
      `Please select/pay ${installmentName} first.`,
      "Sequence Required",
    );
  }

  toggleCollectionInstallment(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
    event: Event,
  ): void {
    event.stopPropagation();

    if (!inst.installmentId || !this.canCollectInstallment(view, inst)) return;

    const checkbox = event.target as HTMLInputElement;
    const fee = this.findFeeByInstallmentIdInView(view, inst.installmentId);

    if (!fee) {
      checkbox.checked = false;
      this.toastr.warning("Fee structure not found for this installment.");
      return;
    }

    if (this.selectedCollectionInstallmentIds.has(inst.installmentId)) {
      this.selectedCollectionInstallmentIds.delete(inst.installmentId);
      this.removeLaterSelectedInstallments(fee, inst);
    } else {
      const requiredPreviousInstallment =
        this.getRequiredPreviousInstallmentForSelection(view, fee, inst);

      if (requiredPreviousInstallment) {
        checkbox.checked = false;
        this.showInstallmentSequenceWarning(requiredPreviousInstallment);
        return;
      }

      this.selectedCollectionInstallmentIds.add(inst.installmentId);
    }

    // Reset custom amount whenever selection changes.
    // This avoids old typed amount being reused after unchecked/rechecked installment.
    this.resetCustomCollectionAmountState();
  }

  selectAllPendingInstallments(view: StudentFeeView): void {
    this.selectedCollectionInstallmentIds.clear();

    const visibleTab = this.getEffectiveFeeDisplayTab(view);

    for (const fee of view.feeStructureList) {
      const isMandatoryFee = Number(fee.isMandatory || 0) === 1;
      if (visibleTab === "mandatory" ? !isMandatoryFee : isMandatoryFee) continue;

      if (this.isOneTimeFee(fee)) {
        if (this.canCollectOneTimeFee(view, fee)) {
          this.selectedCollectionInstallmentIds.add(this.getOneTimeFeeSelectionKey(fee));
        }

        continue;
      }

      for (const inst of fee.installments || []) {
        if (inst.installmentId && this.canCollectInstallment(view, inst)) {
          this.selectedCollectionInstallmentIds.add(inst.installmentId);
        }
      }
    }

    this.resetCustomCollectionAmountState();
  }

  clearSelectedInstallments(): void {
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();
  }

  getInstallmentCollectionAmount(view: StudentFeeView, instId: number): number {
    return Number(
      (
        this.getInstallmentRemainingAmount(view, instId) +
        this.getInstallmentLateFee(view, instId)
      ).toFixed(2),
    );
  }

  getSelectedCollectionCount(): number {
    return this.selectedCollectionInstallmentIds.size;
  }

  getSelectedCollectionAmount(view: StudentFeeView): number {
    let total = 0;

    for (const fee of view.feeStructureList) {
      total += this.getFeeSelectedCollectionAmount(view, fee);
    }

    return Number(total.toFixed(2));
  }

  getFeeSelectedCollectionCount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    const mandatoryRows = (fee as any).__mandatoryAllocationRows as FeePaymentAllocationRow[] | undefined;
    if (mandatoryRows) {
      const selectedRows = (fee as any).__mandatorySelectedRows as FeePaymentAllocationRow[] | undefined;
      return (selectedRows || mandatoryRows).length;
    }

    if (this.isOneTimeFee(fee)) {
      return this.isOneTimeFeeSelected(fee) ? 1 : 0;
    }

    return (fee.installments || []).filter(
      (inst) =>
        inst.installmentId &&
        this.selectedCollectionInstallmentIds.has(inst.installmentId),
    ).length;
  }

  getFeeSelectedCollectionAmount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    const mandatoryRows = (fee as any).__mandatoryAllocationRows as FeePaymentAllocationRow[] | undefined;
    if (mandatoryRows) {
      // The input initially shows the checked installments, not all later payable ones.
      const selectedRows = (fee as any).__mandatorySelectedRows as FeePaymentAllocationRow[] | undefined;
      return Number((selectedRows || mandatoryRows).reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2));
    }

    if (this.isOneTimeFee(fee)) {
      return this.isOneTimeFeeSelected(fee)
        ? Number(this.getFeePending(view, fee).toFixed(2))
        : 0;
    }

    let total = 0;

    for (const inst of fee.installments || []) {
      if (
        inst.installmentId &&
        this.selectedCollectionInstallmentIds.has(inst.installmentId)
      ) {
        total += this.getInstallmentCollectionAmount(view, inst.installmentId);
      }
    }

    return Number(total.toFixed(2));
  }

  getFeeTotalPendingPayable(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    const mandatoryRows = (fee as any).__mandatoryAllocationRows as FeePaymentAllocationRow[] | undefined;
    if (mandatoryRows) {
      // A manual amount can cover subsequent unpaid mandatory installments.
      return Number(mandatoryRows.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2));
    }

    if (this.isOneTimeFee(fee)) {
      return this.canCollectOneTimeFee(view, fee)
        ? Number(this.getFeePending(view, fee).toFixed(2))
        : 0;
    }

    let total = 0;

    for (const inst of fee.installments || []) {
      if (inst.installmentId && this.canCollectInstallment(view, inst)) {
        total += this.getInstallmentCollectionAmount(view, inst.installmentId);
      }
    }

    return Number(total.toFixed(2));
  }

  getCustomCollectionAmountInputValue(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): string | number {
    if (this.isCustomCollectionAmountManual) {
      return this.customCollectionAmountText;
    }

    return this.getFeeSelectedCollectionAmount(view, fee);
  }

  private resetCustomCollectionAmountState(): void {
    this.customCollectionAmount = null;
    this.customCollectionAmountText = "";
    this.isCustomCollectionAmountManual = false;
    this.customAmountExceedsMax = false;
  }

  private setCustomCollectionAmountValue(
    amount: number | null,
    isManual: boolean = true,
  ): void {
    if (amount === null || amount === undefined || Number(amount) <= 0) {
      this.customCollectionAmount = null;
      this.customCollectionAmountText = "";
      this.isCustomCollectionAmountManual = isManual;
      return;
    }

    const fixedAmount = Number(Number(amount).toFixed(2));
    this.customCollectionAmount = fixedAmount;
    this.customCollectionAmountText = String(fixedAmount);
    this.isCustomCollectionAmountManual = isManual;
  }

  onCustomCollectionAmountChange(
    value: any,
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): void {
    this.isCustomCollectionAmountManual = true;

    const rawValue = value === null || value === undefined ? "" : String(value);

    if (rawValue.trim() === "") {
      this.customCollectionAmountText = "";
      this.customCollectionAmount = null;
      return;
    }

    let amount = Number(rawValue);

    if (isNaN(amount)) {
      this.customCollectionAmountText = rawValue;
      this.customCollectionAmount = null;
      return;
    }

    if (amount < 0) {
      amount = 0;
    }

    const maxAmount = this.getFeeTotalPendingPayable(view, fee);

    // Block the value if it exceeds the max — do NOT silently clamp.
    // Set to max and show an inline error flag instead.
    if (amount > maxAmount) {
      this.customCollectionAmountText = rawValue;
      this.customCollectionAmount = null; // keep null so proceed-pay is disabled
      this.customAmountExceedsMax = true;
      return;
    }

    this.customAmountExceedsMax = false;

    if (amount <= 0) {
      this.customCollectionAmountText = rawValue === "0" ? "0" : "";
      this.customCollectionAmount = null;
      return;
    }

    this.customCollectionAmount = Number(amount.toFixed(2));
    this.customCollectionAmountText = rawValue.endsWith(".")
      ? rawValue
      : String(this.customCollectionAmount);
  }

  resetCustomAmountIfInvalid(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): void {
    if (!this.isCustomCollectionAmountManual) return;
    if (!this.customCollectionAmount || this.customCollectionAmount <= 0) return;

    const maxAmount = this.getFeeTotalPendingPayable(view, fee);

    if (this.customCollectionAmount > maxAmount) {
      this.customCollectionAmount = null;
      this.customAmountExceedsMax = true;
    } else {
      this.customAmountExceedsMax = false;
    }
  }

  getFinalCollectionAmount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    if (this.isCustomCollectionAmountManual) {
      const customAmount = Number(this.customCollectionAmount || 0);
      return customAmount > 0 ? Number(customAmount.toFixed(2)) : 0;
    }

    return this.getFeeSelectedCollectionAmount(view, fee);
  }

  private getPaymentAllocationPlanSignature(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): string {
    const installmentSignature = (fee.installments || [])
      .map((inst) => {
        const installmentId = Number(inst.installmentId || 0);
        const status = view.installmentPaymentStatus.get(installmentId);
        const isSelected = installmentId
          ? this.selectedCollectionInstallmentIds.has(installmentId)
          : false;

        return [
          installmentId,
          Number(inst.amount || 0),
          isSelected ? 1 : 0,
          Number(status?.paidAmount || 0),
          Number(status?.remainingAmount ?? inst.amount ?? 0),
          Number(status?.lateFeeAmount || 0),
          status?.isPaid ? 1 : 0,
        ].join(":");
      })
      .join("|");

    const oneTimeStatus = view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0);

    return [
      Number(fee.feeStructureId || 0),
      Number(fee.amount || 0),
      this.isOneTimeFeeSelected(fee) ? 1 : 0,
      this.isCustomCollectionAmountManual ? 1 : 0,
      this.customCollectionAmount ?? "",
      this.customCollectionAmountText,
      Number(oneTimeStatus?.paidAmount || 0),
      Number(oneTimeStatus?.remainingAmount ?? fee.amount ?? 0),
      oneTimeStatus?.isPaid ? 1 : 0,
      installmentSignature,
    ].join("~");
  }

  private cachePaymentAllocationPlan(
    fee: FeeStructureMasterDTO,
    signature: string,
    plan: FeePaymentAllocationRow[],
  ): FeePaymentAllocationRow[] {
    this.paymentAllocationPlanCache.set(fee, { signature, plan });
    return plan;
  }

  getPaymentAllocationPlan(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): FeePaymentAllocationRow[] {
    const mandatoryRows = (fee as any).__mandatoryAllocationRows as FeePaymentAllocationRow[] | undefined;
    if (mandatoryRows) {
      // Default: collect exactly the checked installments. Manual payment may spill
      // forward through later unpaid mandatory installments in installment order.
      const selectedRows = (fee as any).__mandatorySelectedRows as FeePaymentAllocationRow[] | undefined;
      const eligibleRows = this.isCustomCollectionAmountManual
        ? mandatoryRows
        : (selectedRows || mandatoryRows);
      const maxPaise = eligibleRows.reduce(
        (sum, row) => sum + Math.round(row.totalAmount * 100), 0,
      );
      const requestedPaise = this.isCustomCollectionAmountManual
        ? Math.round(Number(this.customCollectionAmount || 0) * 100)
        : maxPaise;
      // Validation above the invoice layer rejects amounts over the payable balance.
      // This guard also prevents over-allocation if a stale amount slips through.
      let amountToAllocatePaise = Math.min(maxPaise, Math.max(0, requestedPaise));
      const result: FeePaymentAllocationRow[] = [];

      for (const row of eligibleRows) {
        if (amountToAllocatePaise <= 0) break;

        const installmentPaise = Math.min(
          amountToAllocatePaise,
          Math.round(row.installmentAmount * 100),
        );
        amountToAllocatePaise -= installmentPaise;
        const lateFeePaise = Math.min(
          amountToAllocatePaise,
          Math.round(row.lateFeeAmount * 100),
        );
        amountToAllocatePaise -= lateFeePaise;

        if (installmentPaise + lateFeePaise > 0) {
          result.push({
            ...row,
            installmentAmount: installmentPaise / 100,
            lateFeeAmount: lateFeePaise / 100,
            totalAmount: (installmentPaise + lateFeePaise) / 100,
          });
        }
      }

      return result;
    }

    const signature = this.getPaymentAllocationPlanSignature(view, fee);
    const cachedPlan = this.paymentAllocationPlanCache.get(fee);

    if (cachedPlan?.signature === signature) {
      return cachedPlan.plan;
    }
    if (this.isOneTimeFee(fee)) {
      if (!fee.feeStructureId || !this.isOneTimeFeeSelected(fee)) {
        return this.cachePaymentAllocationPlan(fee, signature, []);
      }

      const pendingAmount = Number(this.getFeePending(view, fee).toFixed(2));
      const requestedAmount = this.getFinalCollectionAmount(view, fee);
      const payableAmount = Math.min(pendingAmount, requestedAmount);

      if (payableAmount <= 0) {
        return this.cachePaymentAllocationPlan(fee, signature, []);
      }

      return this.cachePaymentAllocationPlan(fee, signature, [
        {
          fee,
          inst: this.createOneTimeFakeInstallment(fee),
          installmentAmount: Number(payableAmount.toFixed(2)),
          lateFeeAmount: 0,
          totalAmount: Number(payableAmount.toFixed(2)),
          isOneTime: true,
        },
      ]);
    }

    const selectedInstallments = (fee.installments || []).filter(
  (inst) =>
    inst.installmentId &&
    this.selectedCollectionInstallmentIds.has(inst.installmentId),
);

if (!selectedInstallments.length) return this.cachePaymentAllocationPlan(fee, signature, []);

const payableInstallments = (fee.installments || []).filter(
  (inst) => inst.installmentId && this.canCollectInstallment(view, inst),
);
    const firstSelectedIndex = payableInstallments.findIndex(
      (inst) =>
        inst.installmentId &&
        this.selectedCollectionInstallmentIds.has(inst.installmentId),
    );

    if (firstSelectedIndex < 0) return this.cachePaymentAllocationPlan(fee, signature, []);

    let amountToAllocate = this.getFinalCollectionAmount(view, fee);

    if (amountToAllocate <= 0) return this.cachePaymentAllocationPlan(fee, signature, []);

    const allocationPlan: FeePaymentAllocationRow[] = [];

    for (let i = firstSelectedIndex; i < payableInstallments.length; i++) {
      if (amountToAllocate <= 0) break;

      const inst = payableInstallments[i];
      const installmentId = inst.installmentId;

      if (!installmentId) continue;

      const remainingAmount = this.getInstallmentRemainingAmount(
        view,
        installmentId,
      );
      const lateFeeAmount = this.getInstallmentLateFee(view, installmentId);

      const installmentPay = Math.min(remainingAmount, amountToAllocate);
      amountToAllocate = Number((amountToAllocate - installmentPay).toFixed(2));

      let lateFeePay = 0;

      if (amountToAllocate > 0 && lateFeeAmount > 0) {
        lateFeePay = Math.min(lateFeeAmount, amountToAllocate);
        amountToAllocate = Number((amountToAllocate - lateFeePay).toFixed(2));
      }

      const totalAmount = Number((installmentPay + lateFeePay).toFixed(2));

      if (totalAmount > 0) {
        allocationPlan.push({
          fee,
          inst,
          installmentAmount: Number(installmentPay.toFixed(2)),
          lateFeeAmount: Number(lateFeePay.toFixed(2)),
          totalAmount,
        });
      }
    }

    return this.cachePaymentAllocationPlan(fee, signature, allocationPlan);
  }

  getAllocatedInstallmentAmount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    return Number(
      this.getPaymentAllocationPlan(view, fee)
        .reduce((sum, item) => sum + item.installmentAmount, 0)
        .toFixed(2),
    );
  }

  getAllocatedLateFeeAmount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    return Number(
      this.getPaymentAllocationPlan(view, fee)
        .reduce((sum, item) => sum + item.lateFeeAmount, 0)
        .toFixed(2),
    );
  }

  getAllocatedTotalAmount(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): number {
    return Number(
      this.getPaymentAllocationPlan(view, fee)
        .reduce((sum, item) => sum + item.totalAmount, 0)
        .toFixed(2),
    );
  }

  collectSingleInstallment(
    view: StudentFeeView,
    inst: FeeInstallmentMasterDTO,
    modalTemplate?: any,
  ): void {
    if (!inst.installmentId || !this.canCollectInstallment(view, inst)) return;

    const fee = this.findFeeByInstallmentIdInView(view, inst.installmentId);

    if (!fee) {
      this.toastr.warning("Fee structure not found for this installment.");
      return;
    }

    const requiredPreviousInstallment =
      this.getRequiredPreviousInstallmentForSelection(view, fee, inst);

    if (requiredPreviousInstallment) {
      this.showInstallmentSequenceWarning(requiredPreviousInstallment);
      return;
    }

    this.selectedCollectionInstallmentIds.clear();
    this.selectedCollectionInstallmentIds.add(inst.installmentId);

    this.setCustomCollectionAmountValue(
      this.getInstallmentCollectionAmount(view, inst.installmentId),
      true,
    );

    if (modalTemplate) {
      this.openCollectFeeModal(view, fee, modalTemplate);
      return;
    }

    this.proceedToCollectPayment(view, fee);
  }

  private findFeeByInstallmentIdInView(
    view: StudentFeeView,
    installmentId: number,
  ): FeeStructureMasterDTO | undefined {
    return view.feeStructureList.find((fee) =>
      (fee.installments || []).some((inst) => inst.installmentId === installmentId),
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fee Collection Bill Helpers
  // ─────────────────────────────────────────────────────────────────────────

  hasFeeBillForInstallment(installmentId: number): boolean {
    return this.feeCollectionBillByInstallmentId.has(installmentId);
  }

  getFeeBillForInstallment(installmentId: number): FeeCollectionBill | null {
    return this.feeCollectionBillByInstallmentId.get(installmentId) || null;
  }

  hasFeeBillForOneTimeFee(fee: FeeStructureMasterDTO): boolean {
    if (!fee?.feeStructureId) return false;
    return this.feeCollectionBillByFeeStructureId.has(fee.feeStructureId);
  }

  getFeeBillForOneTimeFee(fee: FeeStructureMasterDTO): FeeCollectionBill | null {
    if (!fee?.feeStructureId) return null;
    return this.feeCollectionBillByFeeStructureId.get(fee.feeStructureId) || null;
  }

  openOneTimeFeeBill(fee: FeeStructureMasterDTO, modalTemplate: any): void {
    const bill = this.getFeeBillForOneTimeFee(fee);

    if (!bill) {
      this.toastr.warning("Payment bill not found for this fee.", "Bill Missing");
      return;
    }

    this.openFeeBillModal(bill, modalTemplate);
  }

  getInstallmentPaymentMode(installmentId: number): string {
    const bill = this.getFeeBillForInstallment(installmentId);
    return bill?.paymentMethod || "—";
  }

  getInstallmentPaymentModeClass(installmentId: number): string {
    const mode = this.getInstallmentPaymentMode(installmentId).toLowerCase();

    if (mode.includes("cash")) {
      return "afm-payment-mode-cash";
    }

    if (mode.includes("online") || mode.includes("qr") || mode.includes("link")) {
      return "afm-payment-mode-online";
    }

    return "";
  }

  getOneTimeFeePaymentMode(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    const bill = this.getFeeBillForOneTimeFee(fee);

    if (bill?.paymentMethod) {
      return bill.paymentMethod;
    }

    const status = view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0);

    if (status?.paymentMethod) {
      return this.getCollectionMethodLabel(this.normalizeCollectionMethod(status.paymentMethod));
    }

return this.isFeeFullyPaid(view, fee) ? "Online" : "—";
  }

  getOneTimeFeePaymentModeClass(view: StudentFeeView, fee: FeeStructureMasterDTO): string {
    const mode = this.getOneTimeFeePaymentMode(view, fee).toLowerCase();

    if (mode.includes("cash")) {
      return "afm-payment-mode-cash";
    }

    if (mode.includes("online") || mode.includes("qr") || mode.includes("link")) {
      return "afm-payment-mode-online";
    }

    return "";
  }

  private normalizeCollectionMethod(value: any): AdminCollectionMethod {
    const method = String(value || "cash").toLowerCase();

    if (method.includes("qr") || method.includes("online")) {
      return "qr";
    }

    if (method.includes("link")) {
      return "link";
    }

    return "cash";
  }

  openInstallmentBill(installmentId: number, modalTemplate: any): void {
    const bill = this.getFeeBillForInstallment(installmentId);

    if (!bill) {
      this.toastr.warning("Payment bill not found for this installment.", "Bill Missing");
      return;
    }

    this.openFeeBillModal(bill, modalTemplate);
  }

  openFeeBillModal(bill: FeeCollectionBill, modalTemplate?: any): void {
    this.lastGeneratedBill = bill;
    const template = modalTemplate || this.feeBillModalTemplate;

    if (!template) {
      return;
    }

    this.billModalRef = this.modalService.open(template, {
      size: "lg",
      backdrop: "static",
      centered: true,
    });
  }

  private getPaymentDateFromSource(source: any, fallbackDate: Date = new Date()): Date {
    const rawDate =
      source?.paidDate ||
      source?.paymentDate ||
      source?.paymentSuccessDate ||
      source?.transactionDate ||
      source?.pmtDate ||
      source?.updatedOn ||
      source?.updatedAt ||
      source?.otherDetail3 ||
      source?.invoiceDate ||
      source?.createdOn ||
      source?.createdAt ||
      fallbackDate;

    const parsedDate = rawDate instanceof Date ? rawDate : new Date(rawDate);

    if (isNaN(parsedDate.getTime())) {
      return fallbackDate;
    }

    return parsedDate;
  }

  private getReceiptNoFromSource(source: any): string {
    return String(
      source?.prrn ||
        source?.PRRN ||
        source?.receiptNo ||
        source?.invoiceNo ||
        source?.invoiceId ||
        source?.schoolInvoiceId ||
        "",
    );
  }

  private buildPaymentHistoryFromPaidItems(paidItems: any[]): FeePaymentHistoryRow[] {
    return (paidItems || [])
      .filter((item: any) => Number(item?.amount || 0) > 0)
      .map((item: any) => {
        const methodRaw =
          item?.otherDetail2 ||
          item?.paymentMethod ||
          item?.collectionMethod ||
          "cash";

        const paymentDate = this.getPaymentDateFromSource(item);

        return {
          invoiceId: Number(item?.invoiceId || item?.schoolInvoiceId || item?.receiptId || 0),
          receiptNo: this.getReceiptNoFromSource(item),
          amount: Number(Number(item?.amount || 0).toFixed(2)),
          paymentMethod: this.getCollectionMethodLabel(this.normalizeCollectionMethod(methodRaw)),
          paymentDate,
          remarks: item?.remarks || item?.description || "",
        };
      })
      .sort(
        (a: FeePaymentHistoryRow, b: FeePaymentHistoryRow) =>
          new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime(),
      );
  }

  getPaymentHistoryDateText(row: FeePaymentHistoryRow): string {
    if (!row?.paymentDate) {
      return "—";
    }

    const paidDate = new Date(row.paymentDate);

    if (isNaN(paidDate.getTime())) {
      return "—";
    }

    return paidDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  private buildFeeCollectionBill(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    inst: FeeInstallmentMasterDTO,
    invoiceOrDetail: any,
    amount: number,
    status: string = "Paid",
    paymentMethod: string = "Cash",
    paymentHistory: FeePaymentHistoryRow[] = [],
  ): FeeCollectionBill {
    const studentName =
      `${view.student.firstName || ""} ${view.student.middleName || ""} ${view.student.lastName || ""}`.trim() ||
      "Student";

    const invoiceId = Number(
      invoiceOrDetail?.invoiceId ||
        invoiceOrDetail?.receiptId ||
        invoiceOrDetail?.schoolInvoiceId ||
        this.receiptId ||
        0,
    );

    const prrn = String(
      invoiceOrDetail?.prrn ||
        invoiceOrDetail?.PRRN ||
        invoiceOrDetail?.receiptNo ||
        invoiceOrDetail?.invoiceNo ||
        "",
    );

    const invoiceNo = prrn || (invoiceId ? `INV-${invoiceId}` : `BILL-${Date.now()}`);
    const installmentAmount = Number(inst.amount || 0);
    const paidAmount = Number(Number(amount || 0).toFixed(2));
    const remainingAmount = Number(Math.max(0, installmentAmount - paidAmount).toFixed(2));
    const billStatus = remainingAmount > 0 ? "Partial" : status;

    const normalizedMethod = this.getCollectionMethodLabel(
      this.normalizeCollectionMethod(paymentMethod),
    );

    let finalPaymentHistory = [...paymentHistory];

    if (!finalPaymentHistory.length && paidAmount > 0) {
      finalPaymentHistory = [
        {
          invoiceId,
          receiptNo: invoiceNo,
          amount: paidAmount,
          paymentMethod: normalizedMethod,
          paymentDate: this.getPaymentDateFromSource(invoiceOrDetail, new Date()),
          remarks: invoiceOrDetail?.remarks || "Fee payment collected from school admin panel.",
        },
      ];
    }

    finalPaymentHistory = finalPaymentHistory
      .filter((row) => Number(row.amount || 0) > 0)
      .sort(
        (a, b) =>
          new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime(),
      );

    const latestHistory = finalPaymentHistory.length
      ? finalPaymentHistory[finalPaymentHistory.length - 1]
      : null;

    return {
      invoiceId,
      invoiceNo,
      receiptNo: invoiceNo,
      prrn,
      studentId: view.student.userId || 0,
      studentName,
      className: this.selectedClass?.className || "—",
      sectionName: this.selectedSection?.sectionName || "—",
      schoolName: this.schoolName || "School",
      feeName: fee.fName || "Fee",
      installmentName: inst.installmentName || "Installment",
      installmentId: inst.installmentId || 0,
      paymentMethod: latestHistory?.paymentMethod || normalizedMethod,
      amount: paidAmount,
      installmentAmount,
      paidAmount,
      remainingAmount,
      paidDate: latestHistory?.paymentDate || this.getPaymentDateFromSource(invoiceOrDetail, new Date()),
      status: billStatus,
      collectedBy:
        (this.User as any)?.username ||
        (this.User as any)?.firstName ||
        (this.User as any)?.userName ||
        "Admin",
      remarks: invoiceOrDetail?.remarks || "Fee payment collected from school admin panel.",
      paymentHistory: finalPaymentHistory,
    };
  }

  private saveGeneratedBillsForAllocation(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    resMaster: SchoolInvoiceMasterDTO,
    paymentMethod: AdminCollectionMethod,
  ): FeeCollectionBill | null {
    const allocationPlan = this.getPaymentAllocationPlan(view, fee);
    let firstBill: FeeCollectionBill | null = null;
    const collectionDate = new Date();

    allocationPlan.forEach((row) => {
      const receiptNo = String(
        (resMaster as any)?.prrn ||
          (resMaster as any)?.PRRN ||
          (resMaster as any)?.receiptNo ||
          (resMaster as any)?.invoiceNo ||
          (resMaster as any)?.invoiceId ||
          "",
      );

      const currentPaymentHistoryRow: FeePaymentHistoryRow = {
        invoiceId: Number((resMaster as any)?.invoiceId || this.receiptId || 0),
        receiptNo,
        amount: Number(row.installmentAmount.toFixed(2)),
        paymentMethod: this.getCollectionMethodLabel(paymentMethod),
        paymentDate: collectionDate,
        remarks: `Payment collected by ${this.getCollectionMethodLabel(paymentMethod)}`,
      };

      if (row.isOneTime) {
        const feeStructureId = row.fee.feeStructureId;

        if (!feeStructureId || row.totalAmount <= 0) {
          return;
        }

        const existingPaidAmount = this.getFeePaid(view, row.fee);
        const existingBill = this.getFeeBillForOneTimeFee(row.fee);

        const paidAmountAfterCollection = Number(
          (existingPaidAmount + row.installmentAmount).toFixed(2),
        );

        const remainingAmountAfterCollection = Number(
          Math.max(0, Number(row.fee.amount || 0) - paidAmountAfterCollection).toFixed(2),
        );

        const billStatus = remainingAmountAfterCollection > 0 ? "Partial" : "Paid";

        const paymentHistory = [
          ...(existingBill?.paymentHistory || []),
          currentPaymentHistoryRow,
        ];

        const bill = this.buildFeeCollectionBill(
          view,
          row.fee,
          this.createOneTimeFakeInstallment(row.fee),
          {
            ...resMaster,
            paidDate: collectionDate,
            paymentDate: collectionDate,
            otherDetail2: paymentMethod,
            otherDetail3: collectionDate.toISOString(),
          },
          paidAmountAfterCollection,
          billStatus,
          paymentMethod,
          paymentHistory,
        );

        this.feeCollectionBillByFeeStructureId.set(feeStructureId, bill);
        const receiptKey = bill.receiptNo || String(bill.invoiceId || "");
        if (receiptKey) {
          this.sessionReceiptsRegistry.set(receiptKey, bill);
        }

        if (!firstBill) {
          firstBill = bill;
          this.lastGeneratedBill = bill;
        }

        return;
      }

      if (!row.inst.installmentId || row.totalAmount <= 0) {
        return;
      }

      const installmentId = row.inst.installmentId;

      const existingPaidAmount = this.getInstallmentPaidAmount(view, installmentId);
      const existingBill = this.getFeeBillForInstallment(installmentId);

      const paidAmountAfterCollection = Number(
        (existingPaidAmount + row.installmentAmount).toFixed(2),
      );

      const remainingAmountAfterCollection = Number(
        Math.max(0, (row.inst.amount || 0) - paidAmountAfterCollection).toFixed(2),
      );

      const billStatus = remainingAmountAfterCollection > 0 ? "Partial" : "Paid";

      const paymentHistory = [
        ...(existingBill?.paymentHistory || []),
        currentPaymentHistoryRow,
      ];

      const bill = this.buildFeeCollectionBill(
        view,
        row.fee,
        row.inst,
        {
          ...resMaster,
          paidDate: collectionDate,
          paymentDate: collectionDate,
          otherDetail2: paymentMethod,
          otherDetail3: collectionDate.toISOString(),
        },
        paidAmountAfterCollection,
        billStatus,
        paymentMethod,
        paymentHistory,
      );

      this.feeCollectionBillByInstallmentId.set(installmentId, bill);
      const instReceiptKey = bill.receiptNo || String(bill.invoiceId || "");
      if (instReceiptKey) {
        this.sessionReceiptsRegistry.set(instReceiptKey, bill);
      }

      if (!firstBill) {
        firstBill = bill;
        this.lastGeneratedBill = bill;
      }
    });

    const resolvedFirstBill = firstBill as FeeCollectionBill | null;
    const mandatoryAllocation = (fee as any).__mandatoryAllocationRows as FeePaymentAllocationRow[] | undefined;
    if (resolvedFirstBill && mandatoryAllocation && mandatoryAllocation.length > 0 && allocationPlan.length > 0) {
      const paidAllocation = allocationPlan;
      const totalPaid = Number(paidAllocation.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2));
      const baseTotal = Number(paidAllocation.reduce((sum, row) => sum + row.installmentAmount, 0).toFixed(2));
      const installmentLabel = paidAllocation
        .map((row) => row.isOneTime ? "One-time Fee" : (row.inst.installmentName || "Installment"))
        .filter((name, index, all) => all.indexOf(name) === index)
        .join(" + ");

      const combinedBill: FeeCollectionBill = {
        ...resolvedFirstBill,
        feeName: "Mandatory Fees",
        installmentName: installmentLabel || "Combined Installment",
        amount: totalPaid,
        installmentAmount: baseTotal,
        paidAmount: totalPaid,
        remainingAmount: 0,
        status: "Paid",
        feeBreakdown: paidAllocation.map((row) => ({
          feeName: row.fee.fName || "Mandatory Fee",
          installmentName: row.isOneTime ? "One-time Fee" : (row.inst.installmentName || "Installment"),
          amount: Number(row.totalAmount.toFixed(2)),
        })),
        paymentHistory: [{
          invoiceId: resolvedFirstBill.invoiceId,
          receiptNo: resolvedFirstBill.receiptNo,
          amount: totalPaid,
          paymentMethod: this.getCollectionMethodLabel(paymentMethod),
          paymentDate: collectionDate,
          remarks: "Combined mandatory installment collection",
        }],
      };

      this.lastGeneratedBill = combinedBill;
      const receiptKey = combinedBill.receiptNo || String(combinedBill.invoiceId || "");
      if (receiptKey) this.sessionReceiptsRegistry.set(receiptKey, combinedBill);
      firstBill = combinedBill;
    }

    return firstBill;
  }

  getBillDateText(bill: FeeCollectionBill | null): string {
    if (!bill) {
      return "—";
    }

    const latestPayment =
      bill.paymentHistory && bill.paymentHistory.length
        ? bill.paymentHistory[bill.paymentHistory.length - 1]
        : null;

    const paidDate = latestPayment?.paymentDate
      ? new Date(latestPayment.paymentDate)
      : new Date(bill.paidDate);

    if (isNaN(paidDate.getTime())) {
      return "—";
    }

    return paidDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  getBillDownloadFileName(): string {
    const bill = this.lastGeneratedBill;
    const receiptNo = (bill?.receiptNo || "fee-receipt").replace(/[^a-zA-Z0-9_-]/g, "-");
    return `${receiptNo}.html`;
  }

  private getFeeBillHtml(bill: FeeCollectionBill): string {
    const amountText = `₹${Number(bill.paidAmount || bill.amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

    const remainingText = `₹${Number(bill.remainingAmount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

    const installmentAmountText = `₹${Number(bill.installmentAmount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

    const feeBreakdownHtml = (bill.feeBreakdown || []).map((item) => {
      const safeName = String(item.feeName || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const safeInstallment = String(item.installmentName || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const amount = Number(item.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      return `<tr><td>${safeName}</td><td>${safeInstallment}</td><td class="right">₹${amount}</td></tr>`;
    }).join("");

    const paymentHistoryRowsHtml = (bill.paymentHistory || [])
      .map(
        (row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${this.getPaymentHistoryDateText(row)}</td>
            <td>${row.paymentMethod || "—"}</td>
            <td class="right">₹${Number(row.amount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}</td>
          </tr>
        `,
      )
      .join("");

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${bill.receiptNo}</title>
  <style>
    body{font-family:Arial,sans-serif;background:#f5f7fb;margin:0;padding:24px;color:#111827}
    .receipt{max-width:760px;margin:0 auto;background:#fff;border-radius:18px;box-shadow:0 18px 50px rgba(15,23,42,.12);overflow:hidden;border:1px solid #e5e7eb}
    .header{background:linear-gradient(135deg,#1a1a4e,#ad035d);color:#fff;padding:22px 26px;display:flex;justify-content:space-between;gap:14px}
    .header h1{font-size:22px;margin:0 0 6px}.header p{margin:0;font-size:13px;opacity:.9}.badge{background:#dcfce7;color:#166534;padding:6px 12px;border-radius:99px;font-weight:800;font-size:12px;align-self:flex-start}
    .section{padding:22px 26px;border-bottom:1px solid #eef2f7}.grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}.label{font-size:11px;color:#64748b;text-transform:uppercase;font-weight:800;letter-spacing:.05em;margin-bottom:4px}.value{font-size:14px;color:#111827;font-weight:700}.amountgrid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px}.amountbox{background:#f8fafc;border:1px solid #e5e7eb;border-radius:14px;padding:14px}.amountbox .label{margin-bottom:8px}.amount{font-size:26px;color:#1a1a4e;font-weight:900}.balance{font-size:26px;color:#dc2626;font-weight:900}.paidok{font-size:26px;color:#047857;font-weight:900}.footer{padding:18px 26px;font-size:12px;color:#64748b;background:#f8fafc}.history{width:100%;border-collapse:collapse;margin-top:12px}.history th,.history td{border:1px solid #e5e7eb;padding:8px;font-size:12px;text-align:left}.history th{background:#f8fafc;color:#64748b;text-transform:uppercase}.right{text-align:right!important}
    @media print{body{background:#fff;padding:0}.receipt{box-shadow:none;border-radius:0}}
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <div>
        <h1>${bill.schoolName}</h1>
        <p>Fee Payment Receipt</p>
        <p>Receipt No: <strong>${bill.receiptNo}</strong></p>
      </div>
      <div class="badge">${bill.status}</div>
    </div>
    <div class="section grid">
      <div><div class="label">Student</div><div class="value">${bill.studentName}</div></div>
      <div><div class="label">Class / Section</div><div class="value">${bill.className} / ${bill.sectionName}</div></div>
      <div><div class="label">Fee</div><div class="value">${bill.feeName}</div></div>
      <div><div class="label">Installment</div><div class="value">${bill.installmentName}</div></div>
      <div><div class="label">Payment Method</div><div class="value">${bill.paymentMethod}</div></div>
      <div><div class="label">Paid Date</div><div class="value">${this.getBillDateText(bill)}</div></div>
    </div>
    <div class="section amountgrid">
      <div class="amountbox">
        <div class="label">Installment Amount</div>
        <div class="amount">${installmentAmountText}</div>
      </div>
      <div class="amountbox">
        <div class="label">Paid Amount</div>
        <div class="paidok">${amountText}</div>
      </div>
      <div class="amountbox">
        <div class="label">Remaining Balance</div>
        <div class="balance">${remainingText}</div>
      </div>
    </div>
    ${feeBreakdownHtml ? `<div class="section">
      <div class="label">Mandatory Fee Breakdown</div>
      <table class="history"><thead><tr><th>Fee</th><th>Installment</th><th class="right">Paid</th></tr></thead>
        <tbody>${feeBreakdownHtml}</tbody>
      </table>
    </div>` : ""}
    <div class="section">
      <div class="label">Payment History</div>
      <table class="history">
        <thead>
          <tr>
            <th>#</th>
            <th>Date & Time</th>
            <th>Payment Mode</th>
            <th class="right">Paid Amount</th>
          </tr>
        </thead>
        <tbody>
          ${paymentHistoryRowsHtml || `<tr><td colspan="4">No payment history found.</td></tr>`}
        </tbody>
      </table>
    </div>
    <div class="footer">
      Generated by ${bill.collectedBy}. This is a system generated receipt for school fee collection.
    </div>
  </div>
</body>
</html>`;
  }

  downloadFeeBill(): void {
    if (!this.lastGeneratedBill) {
      this.toastr.warning("Bill not available for download.", "Bill Missing");
      return;
    }

    const billHtml = this.getFeeBillHtml(this.lastGeneratedBill);
    const blob = new Blob([billHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = this.getBillDownloadFileName();
    anchor.click();
    URL.revokeObjectURL(url);
  }

  printFeeBill(): void {
    if (!this.lastGeneratedBill) {
      this.toastr.warning("Bill not available for printing.", "Bill Missing");
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      this.toastr.warning("Popup blocked. Please allow popups and try again.", "Popup Blocked");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(this.getFeeBillHtml(this.lastGeneratedBill));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 400);
  }

  async sendFeeBillToParent(): Promise<void> {
    if (!this.lastGeneratedBill) {
      this.toastr.warning("Bill not available for sending.", "Bill Missing");
      return;
    }

    this.isSendingFeeBill = true;

    try {
      const bill = this.lastGeneratedBill;
      const parents = await this.fetchParentsForStudent(bill.studentId);
      const parentEmails = parents
        .map((p) => p.emailId)
        .filter((email) => !!email && email.trim() !== "") as string[];
      const toEmails = environment.production ? parentEmails : ["globalnishwikdev@gmail.com"];

      if (!toEmails.length) {
        this.toastr.warning("Parent email not found. Please download and share manually.", "Email Missing");
        return;
      }

      const amountText = `₹${Number(bill.amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
      const billHtml = this.getFeeBillHtml(bill);

      await Promise.all(
        toEmails.map((email) =>
          this.notificationMasterService
            .NotificationMaster_Create({
              alertConfigId: environment.alertConfigId,
              nType: "Email",
              nSubject: `Fee Receipt — ${bill.studentName} — ${amountText}`,
              nContent: billHtml,
              nTo: email,
              nCc: "",
              nBcc: "",
              nStatus: "Pending",
              remarks: "Fee receipt sent from admin collection screen.",
              attachmentPath: "",
              scheduleddate: new Date(),
              retryCount: 1,
              retryDate: new Date(),
              postSendDataSourceType: "",
              postSendDataSourceDef: "",
              dbConnId: 0,
              actionUser: this.User.userId.toString(),
            })
            .toPromise(),
        ),
      );

      this.toastr.success("Payment bill sent successfully.", "Sent");
    } catch (err) {
      console.error("Failed to send fee bill", err);
      this.toastr.error("Failed to send payment bill. Please try again.", "Error");
    } finally {
      this.isSendingFeeBill = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cash Collection Idempotency — Web Frontend
  // ─────────────────────────────────────────────────────────────────────────

  private getCashCollectionStorageKey(studentId: number): string {
    return `school-cash-pending-${this.schoolId}-${studentId}`;
  }

  private getCashAllocationSignature(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): string {
    return JSON.stringify(
      this.getPaymentAllocationPlan(view, fee).map((row) => ({
        masterType: row.isOneTime ? "FEE_DETAIL" : "Installment",
        masterId: Number(row.isOneTime ? row.fee.feeStructureId : row.inst.installmentId),
        amount: Number(row.installmentAmount.toFixed(2)),
        lateFeeAmount: Number(row.lateFeeAmount.toFixed(2)),
      })),
    );
  }

  private newCashIdempotencyKey(): string {
    // Cryptographically random UUID v4. No timestamp-only or Math.random keys.
    const secureCrypto = window.crypto;
    if (!secureCrypto?.getRandomValues) {
      throw new Error("Secure browser context is required for cash collection.");
    }

    const bytes = new Uint8Array(16);
    secureCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  private readPendingCashCollection(studentId: number): PendingCashCollectionRecord | null {
    const raw = localStorage.getItem(this.getCashCollectionStorageKey(studentId));
    if (!raw) return null;

    try {
      const record = JSON.parse(raw) as PendingCashCollectionRecord;
      if (
        record.schoolId !== this.schoolId ||
        record.studentId !== studentId ||
        !record.idempotencyKey ||
        !["creatingInvoice", "creatingDetails", "readyForCash"].includes(record.stage)
      ) {
        throw new Error("Invalid pending cash operation record.");
      }
      return record;
    } catch {
      throw new Error(
        "Pending cash payment could not be restored. Check existing invoices before collecting again.",
      );
    }
  }

  private savePendingCashCollection(record: PendingCashCollectionRecord): void {
    localStorage.setItem(this.getCashCollectionStorageKey(record.studentId), JSON.stringify(record));
  }

  private clearPendingCashCollection(studentId: number): void {
    localStorage.removeItem(this.getCashCollectionStorageKey(studentId));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Admin / Owner Collection Method Popup
  // ─────────────────────────────────────────────────────────────────────────

  getCollectionMethodLabel(method: AdminCollectionMethod): string {
    const normalizedMethod = this.normalizeCollectionMethod(method);
    const labels: Record<AdminCollectionMethod, string> = {
      cash: "Cash",
      qr: "Online",
      link: "Online",
    };

    return labels[normalizedMethod] || "Cash";
  }

  getCollectionConfirmButtonText(): string {
    if (this.selectedCollectionMethod === "cash") {
      return "Collect Cash Payment";
    }

    if (this.selectedCollectionMethod === "qr") {
      return "Pay For QR Code";
    }

    return "Send Payment Link";
  }

  selectCollectionMethod(method: AdminCollectionMethod): void {
    this.selectedCollectionMethod = method;

    if (method !== "qr") {
      this.isQrCodeReady = false;
      this.generatedCollectionQrCodeUrl = "";
      this.createdCollectionInvoice = null;
    }

    if (method !== "link") {
      this.generatedCollectionLink = "";
    }
  }

  getCollectionAmountForModal(): number {
    if (!this.pendingCollectionView || !this.pendingCollectionFee) {
      return 0;
    }

    return this.getAllocatedTotalAmount(
      this.pendingCollectionView,
      this.pendingCollectionFee,
    );
  }

  getCollectionStudentNameForModal(): string {
    const student = this.pendingCollectionView?.student;

    if (!student) {
      return "Student";
    }

    return `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim() || "Student";
  }

  openCollectFeeModal(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    modalTemplate: any,
  ): void {
    if (this.getFeeSelectedCollectionCount(view, fee) === 0) {
      this.toastr.warning("Please select at least one fee/installment.", "Warning");
      return;
    }

    const payableAmount = this.getAllocatedTotalAmount(view, fee);

    if (payableAmount <= 0) {
      this.toastr.warning("Please enter valid payment amount.", "Warning");
      return;
    }

    this.pendingCollectionView = view;
    this.pendingCollectionFee = fee;
    this.selectedCollectionMethod = "cash";
    this.generatedCollectionLink = "";
    this.generatedCollectionQrCodeUrl = "";
    this.isQrCodeReady = false;
    this.createdCollectionInvoice = null;

    this.collectionModalRef = this.modalService.open(modalTemplate, {
      size: "lg",
      backdrop: "static",
      centered: true,
    });
  }

  confirmCollectFeesFromModal(): void {
    if (!this.pendingCollectionView || !this.pendingCollectionFee) {
      this.toastr.warning("Please select fee details again.", "Warning");
      return;
    }

    const view = this.pendingCollectionView;
    const fee = this.pendingCollectionFee;
    const method = this.selectedCollectionMethod;

    if (method === "cash") {
      this.collectCashPaymentOnSamePage(view, fee);
      return;
    }

    if (method === "qr") {
      this.collectQrPaymentOnSamePage(view, fee);
      return;
    }

    this.sendPaymentLinkFromSamePage(view, fee);
  }

  copyGeneratedPaymentLink(): void {
    if (!this.generatedCollectionLink) {
      this.toastr.warning("Payment link is not generated yet.", "Warning");
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.generatedCollectionLink).then(() => {
        this.toastr.success("Payment link copied successfully.", "Copied");
      });
    } else {
      this.toastr.info(this.generatedCollectionLink, "Payment Link");
    }
  }

  refreshCollectionStatusFromModal(): void {
    const currentStudent =
      this.pendingCollectionView?.student || this.selectedStudentFeeView?.student;

    this.collectionModalRef?.close();
    this.generatedCollectionLink = "";
    this.generatedCollectionQrCodeUrl = "";
    this.isQrCodeReady = false;
    this.createdCollectionInvoice = null;
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();

    if (currentStudent?.userId) {
      this.openStudentFeeDetail(currentStudent, true);
    }
  }

  generateAdminFeeReceiptPayload(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    paymentStatus: "Pending" | "Paid" = "Pending",
    collectionMethod: AdminCollectionMethod = this.selectedCollectionMethod,
  ): {
    master: SchoolInvoiceMasterCreateRequestDTO;
    details: SchoolInvoiceDetailCreateRequestDTO[];
  } {
    const collectionDateIso = new Date().toISOString();
    const allocationPlan = this.getPaymentAllocationPlan(view, fee);
    const selectedAmt = this.getAllocatedTotalAmount(view, fee);

    const studentName = `${view.student.firstName || ""} ${view.student.middleName || ""} ${view.student.lastName || ""}`.trim();

    const master: SchoolInvoiceMasterCreateRequestDTO = {
      invoiceDate: new Date(),
      invoiceAmount: selectedAmt,
      status: paymentStatus,
      schoolId: this.schoolId,
      billedTo: view.student.userId || 0,
      remarks: `Fee collection (${this.getCollectionMethodLabel(collectionMethod)}) — ${studentName || fee.fName || ""}`.trim(),
      otherDetail1: collectionMethod,
      otherDetail2: this.schoolId.toString(),
      otherDetail3: String(this.selectedClass?.classId || 0),
      otherDetail4: "",
      otherDetail5: "",
      otherDetail6: "",
      actionUser: this.User.userId.toString(),
    };

    const details: SchoolInvoiceDetailCreateRequestDTO[] = [];

    allocationPlan.forEach((row) => {
      if (row.isOneTime) {
        if (row.installmentAmount > 0) {
          details.push({
            invoiceId: this.receiptId,
            masterType: "FEE_DETAIL",
            masterId: row.fee.feeStructureId ?? 0,
            amount: row.installmentAmount,
            baseAmount: row.installmentAmount,
            taxType: "",
            taxAmount: 0,
            discountType: "",
            discountAmount: 0,
            description: row.fee.fName || "One-time Fee",
            pmtStatus: paymentStatus,
            otherDetail1: String(row.fee.feeStructureId ?? ""),
            otherDetail2: collectionMethod,
            otherDetail3: collectionDateIso,
            otherDetail4: "",
            otherDetail5: "",
            otherDetail6: "",
            actionUser: this.User.userId.toString(),
          });
        }

        return;
      }

      if (row.installmentAmount > 0) {
        details.push({
          invoiceId: this.receiptId,
          masterType: "Installment",
          masterId: row.inst.installmentId ?? 0,
          amount: row.installmentAmount,
          baseAmount: row.installmentAmount,
          taxType: "",
          taxAmount: 0,
          discountType: "",
          discountAmount: 0,
          description: `${row.fee.fName ?? ""} - ${row.inst.installmentName ?? ""}`,
          pmtStatus: paymentStatus,
          otherDetail1: String(row.fee.feeStructureId ?? ""),
          otherDetail2: collectionMethod,
          otherDetail3: collectionDateIso,
          otherDetail4: "",
          otherDetail5: "",
          otherDetail6: "",
          actionUser: this.User.userId.toString(),
        });
      }

      if (row.lateFeeAmount > 0) {
        details.push({
          invoiceId: this.receiptId,
          masterType: "LateFee",
          masterId: row.inst.installmentId ?? 0,
          amount: row.lateFeeAmount,
          baseAmount: row.lateFeeAmount,
          taxType: "",
          taxAmount: 0,
          discountType: "",
          discountAmount: 0,
          description: `Late Fee for ${row.inst.installmentName ?? ""}`,
          pmtStatus: paymentStatus,
          otherDetail1: String(row.fee.feeStructureId ?? ""),
          otherDetail2: collectionMethod,
          otherDetail3: collectionDateIso,
          otherDetail4: "",
          otherDetail5: "",
          otherDetail6: "",
          actionUser: this.User.userId.toString(),
        });
      }
    });

    return { master, details };
  }

  private async markSchoolInvoiceMasterAsPaid(
    invoiceMaster: SchoolInvoiceMasterDTO,
    collectionMethod: AdminCollectionMethod,
  ): Promise<SchoolInvoiceMasterDTO> {
    if (collectionMethod !== "cash") {
  throw new Error(
    "Only verified cash collections can be marked Paid here."
  );
}
    const invoiceId = Number((invoiceMaster as any)?.invoiceId || 0);

    if (!invoiceId) {
      throw new Error("Created school invoice id is missing.");
    }

    const updatePayload: any = {
      invoiceId,
      schoolId: Number(
        (invoiceMaster as any)?.schoolId || this.schoolId || 0,
      ),
      billedTo: Number(
        (invoiceMaster as any)?.billedTo ||
          this.pendingCollectionView?.student?.userId ||
          this.selectedStudentFeeView?.student?.userId ||
          0,
      ),
      guardian1: (invoiceMaster as any)?.guardian1 ?? null,
      guardian2: (invoiceMaster as any)?.guardian2 ?? null,
      invoiceDate:
        (invoiceMaster as any)?.invoiceDate ||
        (invoiceMaster as any)?.createdOn ||
        new Date(),
      dueDate:
        (invoiceMaster as any)?.dueDate ||
        (invoiceMaster as any)?.invoiceDate ||
        new Date(),
      invoiceAmount: Number(
        (invoiceMaster as any)?.invoiceAmount ??
          (invoiceMaster as any)?.amount ??
          this.getCollectionAmountForModal() ??
          0,
      ),
      status: "Paid",
      remarks:
        (invoiceMaster as any)?.remarks ||
        `Fee collection (${this.getCollectionMethodLabel(collectionMethod)})`,
      prrn:
        (invoiceMaster as any)?.prrn ||
        (invoiceMaster as any)?.PRRN ||
        "",
      invoiceNumber:
        (invoiceMaster as any)?.invoiceNumber ||
        (invoiceMaster as any)?.invoiceNo ||
        "",
      otherDetail1:
        (invoiceMaster as any)?.otherDetail1 || collectionMethod,
      otherDetail2:
        (invoiceMaster as any)?.otherDetail2 || String(this.schoolId),
      otherDetail3:
        (invoiceMaster as any)?.otherDetail3 ||
        String(this.selectedClass?.classId || 0),
otherDetail4: (invoiceMaster as any)?.otherDetail4 || "",
      otherDetail5: (invoiceMaster as any)?.otherDetail5 || "",
      otherDetail6: (invoiceMaster as any)?.otherDetail6 || "",
      isActive: Number((invoiceMaster as any)?.isActive ?? 1),
      actionUser: this.User.userId.toString(),
    };

   
    if (collectionMethod === "cash") {

      const studentId = Number((invoiceMaster as any)?.billedTo || 0);
      const pendingCash = this.readPendingCashCollection(studentId);
      const invoiceAmount = Number(
        (invoiceMaster as any)?.invoiceAmount ??
        (invoiceMaster as any)?.amount ??
        0
      );

      if (
        !pendingCash ||
        pendingCash.invoiceId !== invoiceId ||
        pendingCash.stage !== "readyForCash" ||
        !Number.isFinite(invoiceAmount) ||
        Math.round(pendingCash.amount * 100) !== Math.round(invoiceAmount * 100)
      ) {
        throw new Error("Cash payment identity or invoice amount mismatch. Verify the existing invoice.");
      }

      const cashPaymentPayload = {
        prrn:
          (invoiceMaster as any)?.prrn ||
          (invoiceMaster as any)?.PRRN ||
          null,

        masterType: "SCHOOL",

        masterId: invoiceId,

        // amount: Number(
        //   (invoiceMaster as any)?.invoiceAmount ??
        //   (invoiceMaster as any)?.amount ??
        //   this.getCollectionAmountForModal() ??
        //   0
        // )
        amount: Number(invoiceAmount.toFixed(2)),

        idempotencyKey: pendingCash.idempotencyKey
      };

      if (cashPaymentPayload.amount <= 0) {
        throw new Error("Invalid cash payment amount.");
      }

      await lastValueFrom(
        this.http.post<any>(
          PaymentGateway_InitializeCashPayment,
          cashPaymentPayload
        )
      );

    } else {

      await lastValueFrom(
        this.schoolInvoiceMasterService.SchoolInvoiceMaster_Update(
          updatePayload,
        ),
      );

    }


    this.invoiceMasterLookupCache.delete(invoiceId);

    const updatedInvoiceMaster = await lastValueFrom(
      this.schoolInvoiceMasterService.SchoolInvoiceMaster_ReadById({
        invoiceId,
      }),
    );

    if (!this.isPaidStatus((updatedInvoiceMaster as any)?.status)) {
      throw new Error(
        `Invoice ${invoiceId} was created but its status was not updated to Paid.`,
      );
    }

    return updatedInvoiceMaster;
  }

  private async createCollectionInvoiceAndDetails(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    collectionMethod: AdminCollectionMethod,
    paymentStatus: "Pending" | "Paid",
  ): Promise<SchoolInvoiceMasterDTO | null> {
    const { master, details } = this.generateAdminFeeReceiptPayload(
      view,
      fee,
      paymentStatus,
      collectionMethod,
    );

    let pendingCash: PendingCashCollectionRecord | null = null;

    if (collectionMethod === "cash") {
      if (!details.length) {
        this.toastr.warning("No payment details found.", "Warning");
        return null;
      }

      const studentId = Number(view.student.userId || 0);
      const amount = Number(Number(master.invoiceAmount || 0).toFixed(2));
      if (!studentId || !this.schoolId || !fee.feeStructureId || amount <= 0) {
        throw new Error("Invalid cash payment student, school, fee or amount.");
      }

      const signature = this.getCashAllocationSignature(view, fee);
      pendingCash = this.readPendingCashCollection(studentId);

      if (pendingCash) {
        if (
          pendingCash.feeStructureId !== Number(fee.feeStructureId) ||
          Math.round(pendingCash.amount * 100) !== Math.round(amount * 100) ||
          pendingCash.allocationSignature !== signature
        ) {
          throw new Error(
            `Cash invoice ${pendingCash.invoiceId || "(creation pending)"} has an unresolved attempt. Check it before starting another payment for this student.`,
          );
        }

        if (pendingCash.stage !== "readyForCash" || !pendingCash.invoiceId) {
          throw new Error(
            `Cash invoice ${pendingCash.invoiceId || "(creation uncertain)"} needs reconciliation. Do not create another invoice until its details are checked.`,
          );
        }

        // Reuse the SAME invoice and SAME idempotency key on retry.
        const originalInvoice = await lastValueFrom(
          this.schoolInvoiceMasterService.SchoolInvoiceMaster_ReadById({
            invoiceId: pendingCash.invoiceId,
          }),
        );
        this.receiptId = pendingCash.invoiceId;

        if (this.isPaidStatus((originalInvoice as any)?.status)) {
          this.clearPendingCashCollection(studentId);
          return originalInvoice;
        }

        const paidInvoice = await this.markSchoolInvoiceMasterAsPaid(originalInvoice, "cash");
        this.clearPendingCashCollection(studentId);
        return paidInvoice;
      }

      // Save the intent BEFORE invoice creation, so a lost create response
      // cannot silently trigger another invoice on retry.
      pendingCash = {
        schoolId: this.schoolId,
        studentId,
        feeStructureId: Number(fee.feeStructureId),
        amount,
        allocationSignature: signature,
        invoiceId: 0,
        idempotencyKey: this.newCashIdempotencyKey(),
        stage: "creatingInvoice",
      };
      this.savePendingCashCollection(pendingCash);
    }

    const resMaster: SchoolInvoiceMasterDTO = await lastValueFrom(
      this.schoolInvoiceMasterService.SchoolInvoiceMaster_Create(master),
    );

    this.receiptId = resMaster.invoiceId;

    if (pendingCash) {
      pendingCash.invoiceId = Number(resMaster.invoiceId || 0);
      pendingCash.stage = "creatingDetails";
      this.savePendingCashCollection(pendingCash);
    }

    const receiptDetailsPayload = details.map((d) => ({
      ...d,
      invoiceId: this.receiptId,
    }));

    if (!receiptDetailsPayload.length) {
      this.toastr.warning("No payment details found.", "Warning");
      return null;
    }

    const detailRequests = receiptDetailsPayload.map((d) =>
      this.schoolInvoiceDetailService.SchoolInvoiceDetail_Create(d),
    );

    const resDetails = await lastValueFrom(forkJoin(detailRequests));

    if (!resDetails || resDetails.length !== receiptDetailsPayload.length) {
      this.toastr.error("Failed to create payment details.", "Error");
      return null;
    }

    if (pendingCash) {
      pendingCash.stage = "readyForCash";
      this.savePendingCashCollection(pendingCash);
    }

    let persistedInvoiceMaster = await lastValueFrom(
      this.schoolInvoiceMasterService.SchoolInvoiceMaster_ReadById({
        invoiceId: resMaster.invoiceId,
      }),
    );

      // if (paymentStatus === "Paid" || collectionMethod === "cash") {
      if (collectionMethod === "cash") {
      persistedInvoiceMaster = await this.markSchoolInvoiceMasterAsPaid(
        persistedInvoiceMaster,
        collectionMethod,
      );
      // Clear only after the backend ReadById confirms status Paid.
      if (pendingCash) this.clearPendingCashCollection(pendingCash.studentId);
    }

    return persistedInvoiceMaster;
  }

  private buildCollectionPaymentUrl(
    resMaster: SchoolInvoiceMasterDTO,
    collectionMethod: AdminCollectionMethod,
  ): Promise<string> {
    const payload: ValueListItemReadByVlCodeRequestDTO = { vlCode: "payUrl" };

    return new Promise((resolve, reject) => {
      this.valueListService.ValueListItem_readByVlCode(payload).subscribe({
        next: (valRes) => {
          const baseUrl = valRes.items?.[0]?.vliCode;

          if (!baseUrl) {
            reject(new Error("Payment URL is not configured."));
            return;
          }

          const loanAmount = Number(
            (resMaster as any).invoiceAmount ??
              (resMaster as any).amount ??
              (resMaster as any).invoiceAmt ??
              0,
          );

          const queryParams = new URLSearchParams({
            prrn: String((resMaster as any).prrn || ""),
            userId: String((resMaster as any).billedTo || ""),
            subUserId: String((resMaster as any).billedTo || ""),
            businessId: String(this.schoolId),
            loanamt: String(loanAmount),
            productcode: this.selectedProductCode,
            moduleName: "School",
          });

          queryParams.set("collectionMethod", collectionMethod);
          queryParams.set("paymentMethod", collectionMethod);
          queryParams.set("collectionSource", "school-admin");

          resolve(`${baseUrl}?${queryParams.toString()}`);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  async collectCashPaymentOnSamePage(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): Promise<void> {
    // Guard direct calls and rapid double-clicks before any invoice is created.
    if (this.isNavigating) return;

    if (this.getFeeSelectedCollectionCount(view, fee) === 0) {
      this.toastr.warning("Please select at least one fee/installment.", "Warning");
      return;
    }

    const payableAmount = this.getAllocatedTotalAmount(view, fee);

    if (payableAmount <= 0) {
      this.toastr.warning("Please enter valid payment amount.", "Warning");
      return;
    }

    this.selectedCollectionMethod = "cash";
    this.isNavigating = true;
    this.showPageLoader = true;
    let billToOpen: FeeCollectionBill | null = null;

    try {
      const resMasterUpdated = await this.createCollectionInvoiceAndDetails(
        view,
        fee,
        "cash",
        "Pending",
      );

      if (!resMasterUpdated) {
        return;
      }

      billToOpen = this.saveGeneratedBillsForAllocation(
        view,
        fee,
        resMasterUpdated,
        "cash",
      );

      this.collectionModalRef?.close();
      this.toastr.success("Cash fees collected, payment updated, and bill generated successfully.", "Collected");

      this.selectedCollectionInstallmentIds.clear();
      this.resetCustomCollectionAmountState();
      this.generatedCollectionLink = "";
      this.generatedCollectionQrCodeUrl = "";
      this.isQrCodeReady = false;
      this.createdCollectionInvoice = null;

      await this.reloadStudentFeeDetailAfterCollection(
        view.student,
        true,
      );
    } catch (err) {
      console.error("Cash collection failed", err);
      this.toastr.error(
        err instanceof Error ? err.message : "Cash collection status is uncertain. Verify the existing invoice before retrying.",
        "Error",
      );
    } finally {
      this.isNavigating = false;
      this.showPageLoader = false;

      if (billToOpen) {
        setTimeout(() => this.openFeeBillModal(billToOpen!), 450);
      }
    }
  }

  async collectQrPaymentOnSamePage(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): Promise<void> {
    // QR generation is only a pending payment intent, never proof of collection.
    // Keep existing callers but route them through the safe Pending QR flow.
    await this.generateQrCodeOnSamePage(view, fee);
  }

  async generateQrCodeOnSamePage(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): Promise<void> {
    // Do not create another pending invoice while generating or showing a QR.
    if (this.isNavigating || (this.isQrCodeReady && !!this.generatedCollectionLink)) {
      return;
    }

    if (this.getFeeSelectedCollectionCount(view, fee) === 0) {
      this.toastr.warning("Please select at least one fee/installment.", "Warning");
      return;
    }

    const payableAmount = this.getAllocatedTotalAmount(view, fee);

    if (payableAmount <= 0) {
      this.toastr.warning("Please enter valid payment amount.", "Warning");
      return;
    }

    this.selectedCollectionMethod = "qr";
    this.isNavigating = true;
    this.showPageLoader = true;
    this.generatedCollectionLink = "";
    this.generatedCollectionQrCodeUrl = "";
    this.isQrCodeReady = false;

    try {
      const resMasterUpdated = await this.createCollectionInvoiceAndDetails(
        view,
        fee,
        "qr",
        "Pending",
      );

      if (!resMasterUpdated) {
        return;
      }

      const paymentLink = await this.buildCollectionPaymentUrl(
        resMasterUpdated,
        "qr",
      );

      // The HTML qrcode component renders this link locally.
      this.generatedCollectionLink = paymentLink;
      this.generatedCollectionQrCodeUrl = "";

      this.createdCollectionInvoice = resMasterUpdated;
      this.isQrCodeReady = true;

      this.markPaymentRefreshPending((resMasterUpdated as any).invoiceId);
      this.toastr.success("QR code generated successfully.", "QR Ready");
    } catch (err) {
      console.error("QR generation failed", err);
      this.toastr.error("QR code generation failed. Please try again.", "Error");
    } finally {
      this.isNavigating = false;
      this.showPageLoader = false;
    }
  }

  async sendPaymentLinkFromSamePage(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
  ): Promise<void> {
    if (this.getFeeSelectedCollectionCount(view, fee) === 0) {
      this.toastr.warning("Please select at least one fee/installment.", "Warning");
      return;
    }

    const payableAmount = this.getAllocatedTotalAmount(view, fee);

    if (payableAmount <= 0) {
      this.toastr.warning("Please enter valid payment amount.", "Warning");
      return;
    }

    this.selectedCollectionMethod = "link";
    this.isNavigating = true;
    this.showPageLoader = true;

    try {
      const resMasterUpdated = await this.createCollectionInvoiceAndDetails(
        view,
        fee,
        "link",
        "Pending",
      );

      if (!resMasterUpdated) {
        this.isNavigating = false;
        this.showPageLoader = false;
        return;
      }

      const paymentLink = await this.buildCollectionPaymentUrl(
        resMasterUpdated,
        "link",
      );

      this.generatedCollectionLink = paymentLink;
      await this.sendPaymentLinkToParent(resMasterUpdated, paymentLink);
      this.collectionModalRef?.close();
    } catch (err) {
      console.error("Payment link generation failed", err);
      this.toastr.error("Payment link generation failed. Please try again.", "Error");
      this.isNavigating = false;
      this.showPageLoader = false;
    }
  }

  async proceedToCollectPayment(
    view: StudentFeeView,
    fee: FeeStructureMasterDTO,
    collectionMethod: AdminCollectionMethod = "cash",
  ): Promise<void> {
    if (collectionMethod === "qr") {
      await this.collectQrPaymentOnSamePage(view, fee);
      return;
    }

    if (collectionMethod === "link") {
      await this.sendPaymentLinkFromSamePage(view, fee);
      return;
    }

    await this.collectCashPaymentOnSamePage(view, fee);
  }

  getProductCode(): void {
    const valuelistPayload = { vlCode: "SPE_QuickLoanProduct" };

    this.valueListService.ValueListItem_readByVlCode(valuelistPayload).subscribe({
      next: (res) => {
        if (res.items?.length) {
          this.selectedProductCode = res.items[0].vliCode;
        }
      },
      error: () => {},
    });
  }

  redirectToPayLater(
    resMaster: SchoolInvoiceMasterDTO,
    collectionMethod: AdminCollectionMethod = "cash",
  ): void {
    this.showPageLoader = true;

    this.buildCollectionPaymentUrl(resMaster, collectionMethod)
      .then((redirectUrl) => {
        this.generatedCollectionLink = redirectUrl;

        if (collectionMethod === "link") {
          this.sendPaymentLinkToParent(resMaster, redirectUrl);
          return;
        }

        this.toastr.info(
          "Payment link generated. Collection now continues on this screen.",
          "Payment Link Ready",
        );

        this.isNavigating = false;
        this.showPageLoader = false;
      })
      .catch(() => {
        this.toastr.error("Failed to get payment URL.", "Error");
        this.isNavigating = false;
        this.showPageLoader = false;
      });
  }

  private async sendPaymentLinkToParent(
    resMaster: SchoolInvoiceMasterDTO,
    paymentLink: string,
  ): Promise<void> {
    this.isSendingPaymentLink = true;

    try {
      const student = this.selectedStudentFeeView?.student;
      const billedTo = Number((resMaster as any).billedTo || student?.userId || 0);
      const parents = await this.fetchParentsForStudent(billedTo);
      const studentName = student
        ? `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim()
        : "student";
      const amount = Number(
        (resMaster as any).invoiceAmount ??
          (resMaster as any).amount ??
          (resMaster as any).invoiceAmt ??
          this.getCollectionAmountForModal() ??
          0,
      );
      const amountText = `₹${amount.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;

      const parentEmails = parents
        .map((p) => p.emailId)
        .filter((e) => !!e && e.trim() !== "") as string[];
      const parentMobiles = parents
        .map((p) => p.mobileNo)
        .filter((m) => !!m && m.trim() !== "") as string[];

      const toEmails = environment.production
        ? parentEmails
        : ["globalnishwikdev@gmail.com"];
      const toMobiles = environment.production
        ? parentMobiles
        : ["9699783260"];

      const notificationRequests: Promise<any>[] = [];
      const subject = `Fee Payment Link — ${studentName || "Student"}`;
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; color:#111827; line-height:1.5;">
          <h2 style="margin:0 0 12px;color:#111827;">Fee Payment Link</h2>
          <p>Dear Parent / Guardian,</p>
          <p>A fee payment link has been generated for <strong>${studentName || "Student"}</strong>.</p>
          <p><strong>Amount:</strong> ${amountText}</p>
          <p><a href="${paymentLink}" style="display:inline-block;background:#1a1a4e;color:#ffffff;text-decoration:none;padding:10px 16px;border-radius:8px;">Pay Fees</a></p>
          <p style="font-size:12px;color:#64748b;">If the button does not work, copy and open this link:<br>${paymentLink}</p>
          <p>Regards,<br>${this.schoolName || "School Admin"}</p>
        </div>`;
      const smsContent = `Fee payment link for ${studentName || "student"}. Amount: ${amountText}. Pay here: ${paymentLink} - ${this.schoolName || "School"}`;

      toEmails.forEach((email) => {
        notificationRequests.push(
          this.notificationMasterService
            .NotificationMaster_Create({
              alertConfigId: environment.alertConfigId,
              nType: "Email",
              nSubject: subject,
              nContent: emailHtml,
              nTo: email,
              nCc: "",
              nBcc: "",
              nStatus: "Pending",
              remarks: "Fee payment link generated from admin collection screen.",
              attachmentPath: "",
              scheduleddate: new Date(),
              retryCount: 1,
              retryDate: new Date(),
              postSendDataSourceType: "",
              postSendDataSourceDef: "",
              dbConnId: 0,
              actionUser: this.User.userId.toString(),
            })
            .toPromise(),
        );
      });

      toMobiles.forEach((mobile) => {
        notificationRequests.push(
          this.notificationMasterService
            .NotificationMaster_Create({
              alertConfigId: environment.alertConfigId,
              nType: "SMS",
              nSubject: "Fee Payment Link",
              nContent: smsContent,
              nTo: mobile,
              nCc: "",
              nBcc: "",
              nStatus: "Pending",
              remarks: "Fee payment link generated from admin collection screen.",
              attachmentPath: "",
              scheduleddate: new Date(),
              retryCount: 1,
              retryDate: new Date(),
              postSendDataSourceType: "",
              postSendDataSourceDef: "",
              dbConnId: 0,
              actionUser: this.User.userId.toString(),
            })
            .toPromise(),
        );
      });

      if (!notificationRequests.length) {
        this.toastr.warning(
          "Parent contact not found. Payment link has been generated and copied for manual sharing.",
          "Contact Missing",
        );
        this.copyGeneratedPaymentLink();
      } else {
        await Promise.all(notificationRequests);
        this.toastr.success("Payment link sent successfully.", "Sent");
      }

      this.markPaymentRefreshPending((resMaster as any).invoiceId);
    } catch (err) {
      console.error("Failed to send payment link", err);
      this.toastr.error("Payment link generated, but sending failed.", "Error");
    } finally {
      this.isSendingPaymentLink = false;
      this.isNavigating = false;
      this.showPageLoader = false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Notification — Modal Open Helpers
  // ─────────────────────────────────────────────────────────────────────────

  openSingleNotifyModal(
    student: StudentClassMappingDTO,
    modalTemplate: any,
  ): void {
    this.notifyTarget = "single";
    this.notifySingleStudent = student;
    this.notifyChannels = new Set(["email"]);
    this.bulkSendProgress = 0;
    this.bulkSendSentCount = 0;
    this.notifySubject = "Reminder: Pending Fee Payment";

    this.notifyModalRef = this.modalService.open(modalTemplate, {
      size: "lg",
      backdrop: "static",
    });
  }

  openBulkNotifyModal(modalTemplate: any): void {
    this.notifyTarget = "class";
    this.notifySingleStudent = null;
    this.notifyChannels = new Set(["email"]);
    this.bulkSendProgress = 0;
    this.bulkSendSentCount = 0;
    this.notifySubject = "Reminder: Pending Fee Payment";

    this.bulkNotifyStudents = this.studentList.filter(
      (s) => s.userId && s.userId > 0 && this.studentHasPendingFee(s),
    );

    this.notifyModalRef = this.modalService.open(modalTemplate, {
      size: "lg",
      backdrop: "static",
    });
  }

  toggleNotifyChannel(channel: NotificationChannel): void {
    this.notifyChannels.has(channel)
      ? this.notifyChannels.delete(channel)
      : this.notifyChannels.add(channel);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Notification — Send
  // ─────────────────────────────────────────────────────────────────────────

  sendNotification(): void {
    if (!this.notifyChannels.size) {
      this.toastr.warning("Please select at least one notification channel.");
      return;
    }

    const hasEmail = this.notifyChannels.has("email");
    const hasSms = this.notifyChannels.has("sms");
    const hasPush = this.notifyChannels.has("pushNotification");

    if (!hasEmail && !hasSms && !hasPush) {
      this.toastr.info("SMS and WhatsApp notifications are coming soon.");
      return;
    }

    this.sendAllNotifications(hasEmail, hasSms, hasPush);
  }

  private async sendAllNotifications(
    sendEmail: boolean,
    sendSms: boolean,
    sendPush: boolean,
  ): Promise<void> {
    const targets =
      this.notifyTarget === "single"
        ? this.notifySingleStudent
          ? [this.notifySingleStudent]
          : []
        : this.bulkNotifyStudents;

    if (!targets.length) {
      this.toastr.warning("No recipients to send to.");
      return;
    }

    this.isSendingNotification = true;
    this.bulkSendProgress = 0;
    this.bulkSendSentCount = 0;

    let successCount = 0;

    for (let i = 0; i < targets.length; i++) {
      const student = targets[i];
      const parents = await this.fetchParentsForStudent(student.userId ?? 0);
      const promises: Promise<boolean>[] = [];

      if (sendEmail) {
        promises.push(this.sendEmailForStudent(student, parents));
      }

      if (sendSms) {
        promises.push(this.sendSmsForStudent(student, parents));
      }

      if (sendPush) {
        promises.push(this.sendPushForStudent(student, parents));
      }

      const results = await Promise.allSettled(promises);
      const anySuccess = results.some(
        (r) => r.status === "fulfilled" && r.value === true,
      );

      if (anySuccess) successCount++;

      this.bulkSendSentCount = i + 1;
      this.bulkSendProgress = Math.round(((i + 1) / targets.length) * 100);
    }

    this.isSendingNotification = false;
    this.notifyModalRef?.close();

    if (successCount > 0) {
      this.toastr.success(
        `Notifications sent to ${successCount} student(s) successfully! 🎉`,
        "Sent!",
      );
    } else {
      this.toastr.error(
        "Failed to send notifications. Please check contact details and try again.",
      );
    }
  }

  private fetchParentsForStudent(
    studentId: number,
  ): Promise<ParentStudentMappingDTO[]> {
    if (!studentId) return Promise.resolve([]);

    return this.parentStudentMappingService
      .ParentStudentMapping_ReadByStudentId({ studentId })
      .toPromise()
      .then((res) => res?.items ?? [])
      .catch(() => []);
  }

  private async sendSmsForStudent(
    student: StudentClassMappingDTO,
    parents: ParentStudentMappingDTO[],
  ): Promise<boolean> {
    const parentName =
      parents.length > 0
        ? `${parents[0].firstName || ""} ${parents[0].lastName || ""}`.trim()
        : "Parent / Guardian";

    const studentName =
      `${student.firstName || ""} ${student.lastName || ""}`.trim();

    const feeView =
      this.selectedStudentFeeView?.student?.userId === student.userId
        ? this.selectedStudentFeeView
        : null;

    let earliestDueDate = "—";

    if (feeView && !feeView.noFeeAssigned && !feeView.isLoading) {
      const allPendingDates: Date[] = [];

      for (const fee of feeView.feeStructureList) {
        for (const inst of fee.installments || []) {
          const isPaid = this.isInstallmentPaid(feeView, inst.installmentId!);

          if (!isPaid && inst.paymentDueDate) {
            allPendingDates.push(new Date(inst.paymentDueDate));
          }
        }
      }

      if (allPendingDates.length > 0) {
        allPendingDates.sort((a, b) => a.getTime() - b.getTime());

        const d = allPendingDates[0];

        earliestDueDate = d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    }

    const paymentUrl = `${window.location.origin}/fee-payment`;
    const schoolName = this.schoolName;

    const smsContent =
      `Dear ${parentName}, Fee payment for ${studentName} is pending. ` +
      `Due date: ${earliestDueDate}. ` +
      `Please complete the payment using the link: ${paymentUrl}. ` +
      `Via NSHWIK - ${schoolName}`;

    let toMobiles: string[] = [];

    if (environment.production) {
      const parentMobiles = parents
        .map((p) => p.mobileNo)
        .filter((m) => !!m && m.trim() !== "");

      if (parentMobiles.length > 0) {
        toMobiles = parentMobiles;
      } else if (student.mobileNo?.trim()) {
        toMobiles = [student.mobileNo.trim()];
      }
    } else {
      toMobiles = ["9699783260"];
    }

    if (!toMobiles.length) return false;

    try {
      await Promise.all(
        toMobiles.map((mobile) => {
          const payload: NotificationMasterCreateRequestDTO = {
            alertConfigId: environment.alertConfigId,
            nType: "SMS",
            nSubject: "Late fee notification",
            nContent: smsContent,
            nTo: mobile,
            nCc: "",
            nBcc: "",
            nStatus: "Pending",
            remarks: "",
            attachmentPath: "",
            scheduleddate: new Date(),
            retryCount: 1,
            retryDate: new Date(),
            postSendDataSourceType: "",
            postSendDataSourceDef: "",
            dbConnId: 0,
            actionUser: this.User.userId.toString(),
          };

          return this.notificationMasterService
            .NotificationMaster_Create(payload)
            .toPromise();
        }),
      );

      return true;
    } catch (err) {
      return false;
    }
  }

  private async sendPushForStudent(
    student: StudentClassMappingDTO,
    parents: ParentStudentMappingDTO[],
  ): Promise<boolean> {
    const studentName =
      `${student.firstName || ""} ${student.lastName || ""}`.trim();
    const className = this.selectedClass?.className || "";

    const feeView =
      this.selectedStudentFeeView?.student?.userId === student.userId
        ? this.selectedStudentFeeView
        : null;

    const formatINR = (n: number) =>
      `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    const title = `Fee Reminder — ${studentName} | ${this.schoolName}`;
    const lines: string[] = [];

    const parentName =
      parents.length > 0
        ? `${parents[0].firstName || ""} ${parents[0].lastName || ""}`.trim()
        : "Parent / Guardian";

    lines.push(`Dear ${parentName},`);
    lines.push(
      `Fee payment is pending for ${studentName} (${className}) at ${this.schoolName}.`,
    );
    lines.push("");

    if (feeView && !feeView.noFeeAssigned && !feeView.isLoading) {
      lines.push(
        `Total: ${formatINR(feeView.grandTotal)}  |  ` +
          `Paid: ${formatINR(feeView.grandPaid)}  |  ` +
          `Due: ${formatINR(feeView.grandDue)}`,
      );

      if (feeView.grandLateFees > 0) {
        lines.push(`Late Fees: ${formatINR(feeView.grandLateFees)}`);
      }

      lines.push("");

      for (const fee of feeView.feeStructureList) {
        if (this.isFeeFullyPaid(feeView, fee)) continue;

        const feeName = (fee as any).fName || "Fee";
        const pending = this.getFeePending(feeView, fee);
        const lateFee = this.getFeeLateFees(feeView, fee);
        const isOverdue = this.hasFeeOverdue(feeView, fee);

        const nextDue = (fee.installments || [])
          .filter(
            (i) =>
              !this.isInstallmentPaid(feeView, i.installmentId!) &&
              i.paymentDueDate,
          )
          .map((i) => new Date(i.paymentDueDate!))
          .sort((a, b) => a.getTime() - b.getTime())[0];

        const duePart = nextDue ? ` | Due: ${formatDate(nextDue)}` : "";
        const latePart = lateFee > 0 ? ` + ${formatINR(lateFee)} late fee` : "";
        const statusFlag = isOverdue ? "⚠ OVERDUE" : "⏳ Pending";

        lines.push(
          `${statusFlag} — ${feeName}: ${formatINR(pending)}${latePart}${duePart}`,
        );
      }

      lines.push("");
      lines.push(`Pay now: ${window.location.origin}/fee-payment`);
    } else {
      lines.push(
        `Please log in to view and pay your dues: ${window.location.origin}/fee-payment`,
      );
    }

    const message = lines.join("\n");

    const targetUserIds: string[] =
      parents.length > 0
        ? parents.map((p) => p.parentId.toString())
        : [(student.userId ?? 0).toString()];

    try {
      await Promise.all(
        targetUserIds.map((userId) => {
const payload: PushNotificationSendMessageRequestDTO = {
            userId,
            title,
            message,
          };

          return this.notificationMasterService
            .PushNotificationConfig_SendMessage(payload)
            .toPromise();
        }),
      );

      return true;
    } catch (err) {
      return false;
    }
  }

  private async sendEmailForStudent(
    student: StudentClassMappingDTO,
    parents: ParentStudentMappingDTO[],
  ): Promise<boolean> {
    const emailData = this.buildFeeReminderEmailData(student);
    const htmlContent = feeReminderEmail(emailData);

    let toEmails: string[] = [];

    if (environment.production) {
      const parentEmails = parents
        .map((p) => p.emailId)
        .filter((e) => !!e && e.trim() !== "");

      if (parentEmails.length > 0) {
        toEmails = parentEmails;
      } else if (student.emailId?.trim()) {
        toEmails = [student.emailId.trim()];
      }
    } else {
      toEmails = ["globalnishwikdev@gmail.com"];
    }

    if (!toEmails.length) return false;

    try {
      await Promise.all(
        toEmails.map((email) => {
          const payload: NotificationMasterCreateRequestDTO = {
            alertConfigId: environment.alertConfigId,
            nType: "Email",
            nSubject: this.notifySubject,
            nContent: htmlContent,
            nTo: email,
            nCc: "",
            nBcc: "",
            nStatus: "Pending",
            remarks: "",
            attachmentPath: "",
            scheduleddate: new Date(),
            retryCount: 1,
            retryDate: new Date(),
            postSendDataSourceType: "",
            postSendDataSourceDef: "",
            dbConnId: 0,
            actionUser: this.User.userId.toString(),
          };

          return this.notificationMasterService
            .NotificationMaster_Create(payload)
            .toPromise();
        }),
      );

      return true;
    } catch (err) {
      return false;
    }
  }

  private buildFeeReminderEmailData(
    student: StudentClassMappingDTO,
  ): FeeReminderEmailData {
    const feeView =
      this.selectedStudentFeeView?.student?.userId === student.userId
        ? this.selectedStudentFeeView
        : null;

    const feeDetails: FeeReminderItem[] = [];
    let totalFee = 0;
    let totalPaid = 0;
    let totalDue = 0;

    if (feeView && !feeView.noFeeAssigned && !feeView.isLoading) {
      for (const fee of feeView.feeStructureList) {
        const paid = this.getFeePaid(feeView, fee);
        const pending = this.getFeePending(feeView, fee);
        const lateFee = this.getFeeLateFees(feeView, fee);
        const isOverdue =
          this.hasFeeOverdue(feeView, fee) &&
          !this.isFeeFullyPaid(feeView, fee);

        const feeItem: FeeReminderItem = {
          feeName: (fee as any).fName || (fee as any).feeStructureName || "Fee",
          feeType: this.feeTypeLabels[fee.feeType || ""] || fee.feeType || "",
          totalAmount: fee.amount || 0,
          paidAmount: paid,
          pendingAmount: pending,
          isOverdue,
          lateFee: lateFee > 0 ? lateFee : undefined,
          installments: fee.installments?.map((inst) => {
            const instPaid = this.getInstallmentPaidAmount(
              feeView,
              inst.installmentId!,
            );
            const instRemaining = this.getInstallmentRemainingAmount(
              feeView,
              inst.installmentId!,
            );
            const instPaidFlag = this.isInstallmentPaid(
              feeView,
              inst.installmentId!,
            );
            const instOverdue =
              this.getInstallmentLateFee(feeView, inst.installmentId!) > 0;

            return {
              installmentName: inst.installmentName || "Installment",
              amount: inst.amount || 0,
              paidAmount: instPaid,
              pendingAmount: instRemaining,
              dueDate: inst.paymentDueDate?.toString(),
              isOverdue: instOverdue && !instPaidFlag,
              status: instPaidFlag
                ? "Paid"
                : instOverdue
                  ? "Overdue"
                  : "Pending",
            };
          }),
        };

        feeDetails.push(feeItem);
        totalFee += fee.amount || 0;
        totalPaid += paid;
        totalDue += pending;
      }
    }

    const studentName =
      `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim();

    return {
      parentName: "Parent / Guardian",
      studentName,
      className: this.selectedClass?.className || "",
      sectionName: this.selectedSection?.sectionName,
      schoolName: this.schoolName,
      academicYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
      feeDetails,
      totalFee,
      totalPaid,
      totalDue,
      paymentUrl: `${window.location.origin}/fee-payment`,
      schoolLogoUrl: undefined,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Navigation / Breadcrumb
  // ─────────────────────────────────────────────────────────────────────────

  goBack(): void {
    if (this.currentLevel === "feeDetail") {
      this.goToStudents();
    } else if (this.currentLevel === "students") {
      this.sectionList.length === 0 ? this.goToClasses() : this.goToSections();
    } else if (this.currentLevel === "sections") {
      if (this.enteredClassFromOverview) {
        this.goToOverviewFromClass();
      } else {
        this.goToClasses();
      }
    }
  }

  private goToOverviewFromClass(): void {
    this.goToClasses();
    this.enteredClassFromOverview = false;
    this.switchTab("overview");
  }

  goToClasses(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { classId: null, sectionId: null, studentId: null },
      queryParamsHandling: "merge",
    });

    this.currentLevel = "classes";
    this.selectedClass = null;
    this.selectedSection = null;
    this.studentList = [];
    this.selectedStudentFeeView = null;
    this.classSearchTerm = "";
    this.studentSearchTerm = "";
    this.pendingFeeStudentIds = new Set();
    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
  }

  goToSections(): void {
    if (!this.selectedClass) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        classId: this.selectedClass.classId,
        sectionId: null,
        studentId: null,
      },
      queryParamsHandling: "merge",
    });

    this.currentLevel = "sections";
    this.selectedSection = null;
    this.studentList = [];
    this.selectedStudentFeeView = null;
    this.studentSearchTerm = "";
    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
  }

  goToStudents(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        studentId: null,
      },
      queryParamsHandling: "merge",
    });

    this.currentLevel = "students";
    this.selectedStudentFeeView = null;
    this.isCollectingMode = false;
    this.selectedCollectionInstallmentIds.clear();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Value List
  // ─────────────────────────────────────────────────────────────────────────

  private getValueListItem(vlCode: string): void {
    const data: ValueListItemReadByVlCodeRequestDTO = { vlCode };

    this.valueListService.ValueListItem_readByVlCode(data).subscribe({
      next: (res) => {
        this.feeTypeLabels = {};

        for (const item of res.items) {
          this.feeTypeLabels[item.vliCode] = item.vliName;
        }
      },
      error: () => {},
    });
  }

  getEffectiveFeeDisplayTab(view: StudentFeeView): "mandatory" | "other" {
    if (this.selectedFeeDisplayTab === "mandatory" && view.mandatoryInstallmentRows.length === 0 && view.feeGroups.length > 0) {
      return "other";
    }
    if (this.selectedFeeDisplayTab === "other" && view.feeGroups.length === 0 && view.mandatoryInstallmentRows.length > 0) {
      return "mandatory";
    }
    return this.selectedFeeDisplayTab;
  }

  switchFeeDisplayTab(tab: "mandatory" | "other"): void {
    if (this.selectedFeeDisplayTab === tab) return;
    this.selectedFeeDisplayTab = tab;
    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Mandatory fee collection — one row per installment ordinal.
  // Real fee and installment IDs are retained in every underlying allocation.
  // A one-time mandatory fee is collected in the first combined installment.
  // ─────────────────────────────────────────────────────────────────────────

  private buildMandatoryCombinedInstallments(view: StudentFeeView): MandatoryCombinedInstallment[] {
    const rows = new Map<number, MandatoryCombinedInstallment>();

    const getRow = (number: number): MandatoryCombinedInstallment => {
      let row = rows.get(number);
      if (!row) {
        row = {
          installmentNumber: number,
          installmentName: `Installment ${number}`,
          charges: [],
          amount: 0,
          paid: 0,
          balance: 0,
          lateFee: 0,
          dueDate: "—",
        };
        rows.set(number, row);
      }
      return row;
    };

    for (const fee of view.feeStructureList) {
      if (Number(fee.isMandatory || 0) !== 1) continue;

      if (!fee.installments?.length) {
        const status = view.oneTimeFeePaymentStatus.get(fee.feeStructureId || 0);
        const amount = Number(fee.amount || 0);
        const charge: MandatoryFeeCharge = {
          fee,
          installment: null,
          feeName: fee.fName || "Mandatory Fee",
          amount,
          paid: Number(status?.paidAmount || 0),
          balance: Number(status?.remainingAmount ?? amount),
          lateFee: 0,
          dueDate: "One-time",
          components: [],
        };
        getRow(1).charges.push(charge);
        continue;
      }

      const sortedInstallments = [...fee.installments].sort((a, b) => {
        const aNumber = Number((String(a.installmentName || "").match(/\d+/) || [])[0] || 0);
        const bNumber = Number((String(b.installmentName || "").match(/\d+/) || [])[0] || 0);
        if (aNumber && bNumber) return aNumber - bNumber;
        return 0;
      });

      sortedInstallments.forEach((inst, index) => {
        const status = view.installmentPaymentStatus.get(inst.installmentId || 0);
        const amount = Number(inst.amount || 0);
        const charge: MandatoryFeeCharge = {
          fee,
          installment: inst,
          feeName: fee.fName || "Mandatory Fee",
          amount,
          paid: Number(status?.paidAmount || 0),
          balance: Number(status?.remainingAmount ?? amount),
          lateFee: Number(status?.lateFeeAmount || 0),
          dueDate: this.getInstallmentDueDateText(inst, fee, index, fee.installments?.length || 0),
          components: (inst.components || []).filter((component) =>
            Number(component.isActive ?? 1) !== 0 && Number((component as any).isDeleted ?? 0) !== 1,
          ),
        };
        getRow(index + 1).charges.push(charge);
      });
    }

    return [...rows.values()].sort((a, b) => a.installmentNumber - b.installmentNumber).map((row) => {
      const totalPaise = row.charges.reduce((sum, charge) => sum + Math.round(charge.amount * 100), 0);
      const paidPaise = row.charges.reduce((sum, charge) => sum + Math.round(charge.paid * 100), 0);
      const balancePaise = row.charges.reduce((sum, charge) => sum + Math.round(charge.balance * 100), 0);
      const latePaise = row.charges.reduce((sum, charge) => sum + Math.round(charge.lateFee * 100), 0);
      const dueDates = [...new Set(row.charges.map((charge) => charge.dueDate).filter((date) => date !== "One-time" && date !== "—"))];

      row.amount = totalPaise / 100;
      row.paid = paidPaise / 100;
      row.balance = balancePaise / 100;
      row.lateFee = latePaise / 100;
      row.dueDate = dueDates.length === 1 ? dueDates[0] : (dueDates.length > 1 ? "Multiple dates" : "—");
      return row;
    });
  }

  getMandatoryFeeTotal(view: StudentFeeView): number {
    return view.feeStructureList
      .filter((fee) => Number(fee.isMandatory || 0) === 1)
      .reduce((sum, fee) => sum + Number(fee.amount || 0), 0);
  }

  getMandatoryPaidTotal(view: StudentFeeView): number {
    return Number(view.mandatoryInstallmentRows.reduce((sum, row) => sum + row.paid, 0).toFixed(2));
  }

  getMandatoryPendingTotal(view: StudentFeeView): number {
    return Number(view.mandatoryInstallmentRows.reduce((sum, row) => sum + row.balance + row.lateFee, 0).toFixed(2));
  }

  getMandatoryInstallmentPaymentMode(row: MandatoryCombinedInstallment): string {
    const modes = new Set<string>();
    for (const charge of row.charges) {
      const bill = charge.installment
        ? this.getFeeBillForInstallment(charge.installment.installmentId || 0)
        : this.getFeeBillForOneTimeFee(charge.fee);
      const mode = bill?.paymentMethod || "";
      if (mode && mode !== "—") modes.add(mode);
    }
    if (modes.size === 0) return "—";
    return modes.size === 1 ? [...modes][0] : "Multiple";
  }

  hasMandatoryInstallmentBill(row: MandatoryCombinedInstallment): boolean {
    return row.charges.some((charge) => charge.installment
      ? this.hasFeeBillForInstallment(charge.installment.installmentId || 0)
      : this.hasFeeBillForOneTimeFee(charge.fee));
  }

  private getMandatoryChargeSelectionKey(charge: MandatoryFeeCharge): number {
    return charge.installment?.installmentId || this.getOneTimeFeeSelectionKey(charge.fee);
  }

  isMandatoryCombinedInstallmentSelected(view: StudentFeeView, row: MandatoryCombinedInstallment): boolean {
    const payableCharges = row.charges.filter((charge) => charge.installment
      ? this.canCollectInstallment(view, charge.installment)
      : this.canCollectOneTimeFee(view, charge.fee));
    return payableCharges.length > 0 && payableCharges.every((charge) =>
      this.selectedCollectionInstallmentIds.has(this.getMandatoryChargeSelectionKey(charge)));
  }

  toggleMandatoryCombinedInstallment(view: StudentFeeView, row: MandatoryCombinedInstallment, event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    const payableCharges = row.charges.filter((charge) => charge.installment
      ? this.canCollectInstallment(view, charge.installment)
      : this.canCollectOneTimeFee(view, charge.fee));

    if (checked) {
      // Every underlying fee retains its existing installment order rule.
      for (const charge of payableCharges) {
        if (!charge.installment) continue;
        const prior = this.getRequiredPreviousInstallmentForSelection(view, charge.fee, charge.installment);
        if (prior) {
          (event.target as HTMLInputElement).checked = false;
          this.showInstallmentSequenceWarning(prior);
          return;
        }
      }
    }

    for (const charge of payableCharges) {
      const key = this.getMandatoryChargeSelectionKey(charge);
      if (checked) this.selectedCollectionInstallmentIds.add(key);
      else this.selectedCollectionInstallmentIds.delete(key);
    }
    this.resetCustomCollectionAmountState();
  }

  getSelectedMandatoryInstallmentCount(view: StudentFeeView): number {
    return view.mandatoryInstallmentRows.filter((row) =>
      row.charges.some((charge) => this.selectedCollectionInstallmentIds.has(this.getMandatoryChargeSelectionKey(charge)))).length;
  }

  collectSelectedMandatoryInstallments(view: StudentFeeView, modalTemplate: any): void {
    const selectedFee = this.getMandatorySelectedCollectionFee(view);
    if (!selectedFee) {
      this.toastr.warning("Select at least one Mandatory Fees installment.", "Warning");
      return;
    }
    this.openCollectFeeModal(view, selectedFee, modalTemplate);
  }

  getMandatorySelectedCollectionFee(view: StudentFeeView): FeeStructureMasterDTO | null {
    const allocations: FeePaymentAllocationRow[] = [];
    const selectedAllocations: FeePaymentAllocationRow[] = [];

    // The first checked installment is the starting point. Subsequent unpaid
    // mandatory installments are available only if the admin types a larger amount.
    const firstSelectedRowIndex = view.mandatoryInstallmentRows.findIndex((row) =>
      row.charges.some((charge) =>
        this.selectedCollectionInstallmentIds.has(this.getMandatoryChargeSelectionKey(charge))));
    if (firstSelectedRowIndex < 0) return null;

    for (const row of view.mandatoryInstallmentRows.slice(firstSelectedRowIndex)) {
      for (const charge of row.charges) {
        const isSelected = this.selectedCollectionInstallmentIds.has(this.getMandatoryChargeSelectionKey(charge));
        let allocation: FeePaymentAllocationRow | null = null;

        if (charge.installment && this.canCollectInstallment(view, charge.installment)) {
          const installmentId = charge.installment.installmentId || 0;
          const base = Number(this.getInstallmentRemainingAmount(view, installmentId).toFixed(2));
          const late = Number(this.getInstallmentLateFee(view, installmentId).toFixed(2));
          if (base + late > 0) {
            allocation = {
              fee: charge.fee,
              inst: charge.installment,
              installmentAmount: base,
              lateFeeAmount: late,
              totalAmount: Number((base + late).toFixed(2)),
            };
          }
        } else if (!charge.installment && this.canCollectOneTimeFee(view, charge.fee)) {
          const amount = Number(this.getFeePending(view, charge.fee).toFixed(2));
          if (amount > 0) {
            allocation = {
              fee: charge.fee,
              inst: this.createOneTimeFakeInstallment(charge.fee),
              installmentAmount: amount,
              lateFeeAmount: 0,
              totalAmount: amount,
              isOneTime: true,
            };
          }
        }

        if (allocation) {
          allocations.push(allocation);
          if (isSelected) selectedAllocations.push(allocation);
        }
      }
    }

    if (!selectedAllocations.length) return null;

    return {
      ...selectedAllocations[0].fee,
      fName: "Mandatory Fees",
      amount: Number(selectedAllocations.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2)),
      installments: selectedAllocations.map((row) => row.inst),
      __mandatoryAllocationRows: allocations,
      __mandatorySelectedRows: selectedAllocations,
    } as FeeStructureMasterDTO;
  }

  getMandatoryDetailComponentsTotal(charge: MandatoryFeeCharge): number {
    return Number(charge.components.reduce((sum, item) => sum + Number(item.amount || 0), 0).toFixed(2));
  }

  toggleMandatoryInstallmentDetails(number: number): void {
    if (this.expandedMandatoryInstallmentNumbers.has(number)) {
      this.expandedMandatoryInstallmentNumbers.delete(number);
    } else {
      this.expandedMandatoryInstallmentNumbers.add(number);
    }
  }

  trackByMandatoryInstallment(_: number, row: MandatoryCombinedInstallment): number {
    return row.installmentNumber;
  }

  trackByMandatoryCharge(_: number, charge: MandatoryFeeCharge): string {
    return `${charge.fee.feeStructureId}-${charge.installment?.installmentId ?? "one-time"}`;
  }

  // Existing InvoiceMaster + InvoiceDetail flow accepts multiple detail entries.
  // This snapshot uses actual real installment IDs, not an artificial installment ID.
  collectMandatoryCombinedInstallment(
    view: StudentFeeView,
    row: MandatoryCombinedInstallment,
    modalTemplate: any,
  ): void {
    this.collectMandatoryCombinedRows(view, [row], modalTemplate, false);
  }

  private collectMandatoryCombinedRows(
    view: StudentFeeView,
    rows: MandatoryCombinedInstallment[],
    modalTemplate: any,
    selectedOnly: boolean,
  ): void {
    if (this.isNavigating) return;

    const charges = rows.flatMap((row) => row.charges).filter((charge) => {
      if (charge.balance + charge.lateFee <= 0) return false;
      return !selectedOnly || this.selectedCollectionInstallmentIds.has(this.getMandatoryChargeSelectionKey(charge));
    });
    const requestedInstallmentIds = new Set(charges
      .filter((charge) => charge.installment?.installmentId)
      .map((charge) => Number(charge.installment!.installmentId)));
    const allocations: FeePaymentAllocationRow[] = [];

    for (const charge of charges) {
      if (charge.installment) {
        const inst = charge.installment;
        if (!inst.installmentId || !this.canCollectInstallment(view, inst)) continue;

        // Prevent paying later installments without earlier paid/selected ones.
        const index = (charge.fee.installments || []).findIndex((item) => item.installmentId === inst.installmentId);
        const previous = (charge.fee.installments || [])
          .slice(0, index < 0 ? 0 : index)
          .find((item) => item.installmentId && this.canCollectInstallment(view, item) && !requestedInstallmentIds.has(item.installmentId));
        if (previous) {
          this.toastr.warning(`Pay ${charge.feeName} - ${previous.installmentName} first.`, "Sequence Required");
          return;
        }

        const base = Number(this.getInstallmentRemainingAmount(view, inst.installmentId).toFixed(2));
        const late = Number(this.getInstallmentLateFee(view, inst.installmentId).toFixed(2));
        allocations.push({
          fee: charge.fee,
          inst,
          installmentAmount: base,
          lateFeeAmount: late,
          totalAmount: Number((base + late).toFixed(2)),
        });
      } else if (this.canCollectOneTimeFee(view, charge.fee)) {
        const amount = Number(this.getFeePending(view, charge.fee).toFixed(2));
        allocations.push({
          fee: charge.fee,
          inst: this.createOneTimeFakeInstallment(charge.fee),
          installmentAmount: amount,
          lateFeeAmount: 0,
          totalAmount: amount,
          isOneTime: true,
        });
      }
    }

    if (!allocations.length) {
      this.toastr.warning("No outstanding Mandatory Fees in the selected installment(s).", "Warning");
      return;
    }

    const totalPaise = allocations.reduce((sum, item) => sum + Math.round(item.totalAmount * 100), 0);
    const total = totalPaise / 100;
    // A temporary payment aggregate; all InvoiceDetail rows use real source master IDs.
    const composite = {
      ...allocations[0].fee,
      fName: "Mandatory Fees",
      amount: total,
      installments: allocations.map((item) => item.inst),
      __mandatoryAllocationRows: allocations,
    } as FeeStructureMasterDTO;

    this.selectedCollectionInstallmentIds.clear();
    this.resetCustomCollectionAmountState();
    for (const item of allocations) {
      const key = item.isOneTime
        ? this.getOneTimeFeeSelectionKey(item.fee)
        : Number(item.inst.installmentId || 0);
      this.selectedCollectionInstallmentIds.add(key);
    }

    this.openCollectFeeModal(view, composite, modalTemplate);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Misc Helpers
  // ─────────────────────────────────────────────────────────────────────────

  getGroupTotal(group: FeeGroup): number {
    return group.fees.reduce((total, fee) => total + (fee.amount || 0), 0);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TrackBy Functions
  // ─────────────────────────────────────────────────────────────────────────

  trackByClassId(_: number, item: ClassMasterDTO): number {
    return item.classId;
  }

  trackBySectionId(_: number, item: SectionMasterDTO): number {
    return item.sectionId;
  }

  trackByStudentId(_: number, item: StudentClassMappingDTO): number {
    return item.userId ?? 0;
  }

  trackByFeeType(_: number, item: FeeGroup): string {
    return item.feeType;
  }

  trackByFeeId(_: number, item: FeeStructureMasterDTO): number {
    return item.feeStructureId;
  }

  trackByInstId(_: number, item: FeeInstallmentMasterDTO): number {
    return item.installmentId ?? 0;
  }

  // Display only: existing installment payment and invoice IDs remain unchanged.
  toggleInstallmentDetails(installmentId: number): void {
    if (this.expandedInstallmentDetailIds.has(installmentId)) {
      this.expandedInstallmentDetailIds.delete(installmentId);
    } else {
      this.expandedInstallmentDetailIds.add(installmentId);
    }
  }

  getInstallmentDetailComponents(inst: FeeInstallmentMasterDTO): FeeComponentMasterDTO[] {
    return (inst.components || []).filter((component) => {
      const detail = component as any;
      return Number(detail.isDeleted ?? 0) !== 1 && Number(detail.isActive ?? 1) !== 0;
    });
  }

  getInstallmentDetailComponentTotal(inst: FeeInstallmentMasterDTO): number {
    const totalPaise = this.getInstallmentDetailComponents(inst).reduce(
      (total, component) => total + Math.round(Number(component.amount || 0) * 100),
      0,
    );
    return totalPaise / 100;
  }

  getInstallmentDetailUnitemizedAmount(inst: FeeInstallmentMasterDTO): number {
    const amountPaise = Math.round(Number(inst.amount || 0) * 100);
    const componentPaise = Math.round(this.getInstallmentDetailComponentTotal(inst) * 100);
    return Math.max(0, amountPaise - componentPaise) / 100;
  }

  trackByPaymentAllocationRow(_: number, item: FeePaymentAllocationRow): string {
    return `${item.isOneTime ? "one-time" : "installment"}-${item.fee.feeStructureId || 0}-${item.inst.installmentId || 0}`;
  }

  private getValidDate(value: any): Date | null {
    if (!value) return null;

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  private getLastDayOfMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  getInstallmentDueDateValue(
    inst: FeeInstallmentMasterDTO,
    fee: FeeStructureMasterDTO,
    instIndex: number,
    totalInstallments: number,
  ): Date | null {
    const installmentAny: any = inst;
    const feeAny: any = fee;

    // 1. First check direct due-date fields from installment object
    const directDueDate =
      installmentAny.paymentDueDate ||
      installmentAny.dueDate ||
      installmentAny.installmentDueDate ||
      installmentAny.dueOn ||
      installmentAny.paymentDueOn ||
      installmentAny.lastPaymentDate ||
      installmentAny.endDate ||
      installmentAny.toDate;

    const directDate = this.getValidDate(directDueDate);

    if (directDate) {
      return directDate;
    }

    // 2. If direct due date is missing, calculate using fee/start date
    const baseStartDate =
      installmentAny.startDate ||
      installmentAny.installmentStartDate ||
      feeAny.startDate ||
      feeAny.fStartDate ||
      feeAny.fromDate ||
      feeAny.effectiveFrom ||
      feeAny.createdOn ||
      feeAny.createdDate;

    const baseDate = this.getValidDate(baseStartDate);

    if (!baseDate) {
      return null;
    }

    const frequencyMonths = Number(
      feeAny.installmentFrequencyMonths ||
      feeAny.frequencyMonths ||
      feeAny.intervalMonths ||
      feeAny.installmentInterval ||
      1,
    );

    const dueDay = Number(
      installmentAny.dueDay ||
      feeAny.dueDay ||
      baseDate.getDate() ||
      1,
    );

    const dueDate = new Date(baseDate);
    dueDate.setDate(1);
    dueDate.setMonth(baseDate.getMonth() + ((instIndex + 1) * frequencyMonths));

    const safeDueDay = Math.min(
      dueDay,
      this.getLastDayOfMonth(dueDate.getFullYear(), dueDate.getMonth()),
    );

    dueDate.setDate(safeDueDay);

    return dueDate;
  }

  getInstallmentDueDateText(
    inst: FeeInstallmentMasterDTO,
    fee: FeeStructureMasterDTO,
    instIndex: number,
    totalInstallments: number,
  ): string {
    const dueDate = this.getInstallmentDueDateValue(
      inst,
      fee,
      instIndex,
      totalInstallments,
    );

    if (!dueDate) {
      return "—";
    }

    return dueDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
}